import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  X,
  Search,
  Camera,
  QrCode,
  Sparkles,
  BookOpen,
  Clock,
  Layers,
  ShieldCheck,
  FileText,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Edit3,
  Check,
  RotateCcw,
  Loader2,
  Utensils,
  Settings,
  Info,
  ExternalLink,
  AlertTriangle,
  Upload,
  Mic,
  MicOff,
  ChevronLeft,
  ChevronRight,
  Users,
  PieChart,
  Package,
  Zap,
  ZapOff,
} from 'lucide-react';
import { useBarcodeScanner } from '../lib/useBarcodeScanner';
import { CameraViewfinder, CameraViewfinderRef } from './CameraViewfinder';
import {
  Meal,
  MealType,
  PlateDish,
  MealIngredientItem,
  SavedRecipe,
  DetailedFodmapStackingReport,
  CustomFoodItem,
  FodmapRating,
} from '../lib/types';
import {
  formatIsoDate,
  getDefaultMealType,
  extractLocalDateFromIso,
  extractLocalTimeFromIso,
  createIsoFromLocalDateAndTime,
} from '../lib/dateUtils';
import { compressImage } from '../lib/imageUtils';
import {
  canonicalizeIngredient,
  formatIngredientTitle,
  fetchProductByBarcode,
  searchOpenFoodFacts,
  OpenFoodFactsProductResult,
  extractRecipeServingsAndPortion,
  formatServingMultiplierLabel,
  synthesizeDishTitle,
  synthesizeMealDescription,
  normalizeParsedAiIngredients,
} from '../lib/ingredientUtils';
import { localStore } from '../lib/storage';
import { analyzeImageDirectOrProxy, describeMealDirectOrProxy } from '../lib/gemini';
import {
  calculatePlateFodmapStacking,
  convertTagsToIngredientItems,
  getRealisticCulinaryServing,
  getDynamicIngredientRating,
  searchFodmapDatabase,
  initializeFodmapDatabase,
  matchIngredientToFodmap,
  getSafeLimitNote,
  getFoodPortionConfig,
  formatIngredientPortion,
  formatPortionButton,
  saveCustomFoodItem,
  derivePrimaryCulprit,
  FodmapFoodItem,
  autoRegisterDiscoveredFoods,
  calculateDetailedFodmapSideScale,
  getSafePortionBrackets,
  scaleDishTotalWeight,
} from '../lib/fodmapEngine';
import { FodmapStackingDetailModal } from './FodmapStackingDetailModal';
import { Sheet } from './Sheet';

interface UnifiedMealLoggerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveMeal: (meal: Omit<Meal, 'id'>) => void;
  onDeleteMeal?: (mealId: string) => Promise<void> | void;
  initialMeal?: Meal | null;
  initialTab?: TabMode;
  defaultMealType?: MealType;
  recentMeals?: Meal[];
}

type TabMode = 'search' | 'scan' | 'ai' | 'library' | null;
type ScanSubTab = 'barcode' | 'label';
type AiSubTab = 'snap' | 'describe';

const MEAL_CATEGORIES: MealType[] = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Drink'];

const FREE_FROM_OPTIONS = [
  'Gluten-Free',
  'Dairy-Free',
  'Garlic-Free',
  'Onion-Free',
  'Nut-Free',
  'Soy-Free',
  'Egg-Free',
  'FODMAP-Certified',
];

interface IngredientPortionModalProps {
  editingIngredient: {
    dishIndex: number;
    ingIndex: number;
    id?: string;
    name: string;
    amount: number;
    unit: string;
    gramsEstimate: number;
    servingLimitNote?: string;
    rating?: FodmapRating;
    primaryCulprit?: string;
  };
  onClose: () => void;
  onSave: (amount: number, unit: string, grams: number) => void;
  onSwapIngredient?: (dishIndex: number, ingIndex: number, newFood: FodmapFoodItem) => void;
  onSaveCustomToBase?: (dishIndex: number, ingIndex: number, food: CustomFoodItem) => void;
}

const IngredientPortionModal: React.FC<IngredientPortionModalProps> = ({
  editingIngredient,
  onClose,
  onSave,
  onSwapIngredient,
  onSaveCustomToBase,
}) => {
  const [activeTab, setActiveTab] = useState<'portion' | 'swap' | 'addToBase'>('portion');

  // Portion State
  const portionConfig = useMemo(() => getFoodPortionConfig(editingIngredient.name), [editingIngredient.name]);
  const currentUnitLower = (editingIngredient.unit || '').toLowerCase().trim();

  const [selectedUnit, setSelectedUnit] = useState<string>(() => {
    const matched = portionConfig.units.find(
      (u) =>
        u.unit.toLowerCase() === currentUnitLower ||
        u.unit.toLowerCase() + 's' === currentUnitLower ||
        u.label.toLowerCase() === currentUnitLower
    );
    return matched ? matched.unit : portionConfig.units[0]?.unit || 'g';
  });

  const activeUnitDef = useMemo(() => {
    return (
      portionConfig.units.find((u) => u.unit.toLowerCase() === selectedUnit.toLowerCase()) ||
      portionConfig.units[0] || { unit: 'g', label: 'Grams', gramsPerUnit: 1, step: 10 }
    );
  }, [portionConfig, selectedUnit]);

  const initialAmount = useMemo(() => {
    if (typeof editingIngredient.amount === 'number' && !isNaN(editingIngredient.amount) && editingIngredient.amount > 0) {
      return editingIngredient.amount;
    }
    if (editingIngredient.gramsEstimate && activeUnitDef.gramsPerUnit) {
      return Math.round((editingIngredient.gramsEstimate / activeUnitDef.gramsPerUnit) * 10) / 10;
    }
    return 1;
  }, [editingIngredient, activeUnitDef]);

  const [amount, setAmount] = useState<number>(initialAmount);
  const [amountInputText, setAmountInputText] = useState<string>(String(initialAmount));

  const calculatedGrams = useMemo(() => {
    return Math.max(1, Math.round(amount * activeUnitDef.gramsPerUnit));
  }, [amount, activeUnitDef]);

  const handleTextChange = (raw: string) => {
    setAmountInputText(raw);
    const parsed = parseFloat(raw);
    if (!isNaN(parsed) && parsed > 0) {
      setAmount(parsed);
    }
  };

  const handleBlur = () => {
    const parsed = parseFloat(amountInputText);
    if (isNaN(parsed) || parsed <= 0) {
      setAmountInputText(String(amount));
    } else {
      setAmount(parsed);
      setAmountInputText(String(parsed));
    }
  };

  const handleStep = (delta: number) => {
    const step = activeUnitDef.step || 1;
    const nextAmount = Math.max(step < 1 ? step : 1, Math.round((amount + delta * step) * 100) / 100);
    setAmount(nextAmount);
    setAmountInputText(String(nextAmount));
  };

  const handleSelectUnit = (unitDef: typeof activeUnitDef) => {
    setSelectedUnit(unitDef.unit);
    const newAmount = Math.max(1, Math.round((calculatedGrams / unitDef.gramsPerUnit) * 10) / 10);
    setAmount(newAmount);
    setAmountInputText(String(newAmount));
  };

  const handleSetPreset = (presetAmount: number, presetUnit: string) => {
    setSelectedUnit(presetUnit);
    setAmount(presetAmount);
    setAmountInputText(String(presetAmount));
  };

  const handleAutoTuneSafe = (safeGrams: number) => {
    const safeAmount = activeUnitDef.gramsPerUnit
      ? Math.max(0.25, Math.round((safeGrams / activeUnitDef.gramsPerUnit) * 10) / 10)
      : safeGrams;
    setAmount(safeAmount);
    setAmountInputText(String(safeAmount));
  };

  // Swap Search State
  const [swapSearchQuery, setSwapSearchQuery] = useState('');
  const swapSearchResults = useMemo(() => {
    return searchFodmapDatabase(swapSearchQuery || editingIngredient.name, 16);
  }, [swapSearchQuery, editingIngredient.name]);

  // Add to Base State
  const [customName, setCustomName] = useState(editingIngredient.name);
  const [customCategory, setCustomCategory] = useState('other');
  const [customRating, setCustomRating] = useState<FodmapRating>(editingIngredient.rating || 'low');
  const [customCulprit, setCustomCulprit] = useState<string>(editingIngredient.primaryCulprit || 'None');
  const [customSafeGrams, setCustomSafeGrams] = useState<string>(String(editingIngredient.gramsEstimate || 50));
  const [customNotes, setCustomNotes] = useState(editingIngredient.servingLimitNote || '');

  const handleSaveToBase = () => {
    const gramsNum = parseFloat(customSafeGrams) || 50;
    const customItem: CustomFoodItem = {
      id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: customName.trim() || editingIngredient.name,
      category: customCategory,
      rating: customRating,
      primaryCulprit: customCulprit === 'None' ? undefined : customCulprit,
      safeGrams: gramsNum,
      safeServingTitle: `${gramsNum}g`,
      notes: customNotes.trim() || undefined,
      createdAtIso: new Date().toISOString(),
    };
    saveCustomFoodItem(customItem);
    if (onSaveCustomToBase) {
      onSaveCustomToBase(editingIngredient.dishIndex, editingIngredient.ingIndex, customItem);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/75 p-4 backdrop-blur-sm animate-in fade-in duration-150">
      <div className="w-full max-w-sm max-h-[90vh] overflow-y-auto bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-2xl p-5 space-y-4 shadow-2xl text-slate-900 dark:text-zinc-100">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="truncate pr-2">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-zinc-100 truncate">
              {editingIngredient.name}
            </h3>
            <p className="text-xs text-slate-500 dark:text-zinc-400">
              {activeTab === 'portion' ? 'Portion & Conversion' : activeTab === 'swap' ? 'Swap with Verified Food' : 'Add to FODMAP Base List'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 dark:text-zinc-400 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Selector */}
        <div className="flex p-1 bg-slate-100 dark:bg-zinc-900 rounded-xl border border-slate-200/60 dark:border-zinc-800 text-xs font-semibold">
          <button
            type="button"
            onClick={() => setActiveTab('portion')}
            className={`flex-1 py-1.5 rounded-lg transition-colors ${
              activeTab === 'portion'
                ? 'bg-white dark:bg-zinc-800 text-slate-900 dark:text-zinc-100 shadow-xs'
                : 'text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-zinc-300'
            }`}
          >
            Portion & Units
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('swap')}
            className={`flex-1 py-1.5 rounded-lg transition-colors ${
              activeTab === 'swap'
                ? 'bg-white dark:bg-zinc-800 text-slate-900 dark:text-zinc-100 shadow-xs'
                : 'text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-zinc-300'
            }`}
          >
            Smart Swaps
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('addToBase')}
            className={`flex-1 py-1.5 rounded-lg transition-colors ${
              activeTab === 'addToBase'
                ? 'bg-white dark:bg-zinc-800 text-slate-900 dark:text-zinc-100 shadow-xs'
                : 'text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-zinc-300'
            }`}
          >
            + Add to List
          </button>
        </div>

        {/* VIEW 1: PORTION EDITOR */}
        {activeTab === 'portion' && (
          <div className="space-y-4">
            {/* Safe Limit Reference Card */}
            <div className="p-3 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400">
                  Serving & Safe Limit
                </span>
                <span className="text-[10px] font-mono text-slate-400 dark:text-zinc-500">
                  Standardized
                </span>
              </div>
              <div className="text-xs font-semibold text-slate-800 dark:text-zinc-200">
                {portionConfig.monashServingText}
              </div>
              {editingIngredient.servingLimitNote &&
                editingIngredient.servingLimitNote !== portionConfig.monashServingText && (
                  <div className="text-[11px] text-slate-500 dark:text-zinc-400 pt-0.5">
                    {editingIngredient.servingLimitNote}
                  </div>
                )}

              {portionConfig.monashSafeGrams !== undefined && portionConfig.monashSafeGrams > 0 && (
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => handleAutoTuneSafe(portionConfig.monashSafeGrams!)}
                    className="w-full min-h-[40px] px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-300 dark:border-emerald-800/80 text-emerald-800 dark:text-emerald-200 text-xs font-bold flex items-center justify-between hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition-colors shadow-xs"
                  >
                    <span className="flex items-center gap-1.5 truncate">
                      <Check className="w-3.5 h-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                      <span className="truncate">Set to Safe Portion ({portionConfig.monashSafeGrams}g)</span>
                    </span>
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-200/60 dark:bg-emerald-900/80 text-emerald-900 dark:text-emerald-100 font-semibold shrink-0">
                      AUTO-TUNE
                    </span>
                  </button>
                </div>
              )}
            </div>

            {/* Unit Selector Pills */}
            {portionConfig.units.length > 1 && (
              <div>
                <label className="block text-[11px] font-medium text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wider">
                  Select Unit
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {portionConfig.units.map((unitDef) => {
                    const isSelected = activeUnitDef.unit.toLowerCase() === unitDef.unit.toLowerCase();
                    return (
                      <button
                        key={unitDef.unit}
                        type="button"
                        onClick={() => handleSelectUnit(unitDef)}
                        className={`min-h-[38px] px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          isSelected
                            ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-sm'
                            : 'bg-slate-100 dark:bg-zinc-900 text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-800 border border-transparent'
                        }`}
                      >
                        {unitDef.label}
                        {unitDef.unit !== 'g' && (
                          <span className="text-[10px] opacity-60 ml-1 font-normal">
                            ({unitDef.gramsPerUnit}g)
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Stepper & Quantity Input */}
            <div>
              <label className="block text-slate-600 dark:text-zinc-400 mb-1.5 text-xs font-medium">
                Quantity ({activeUnitDef.label})
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleStep(-1)}
                  className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-lg transition-colors shadow-xs"
                  aria-label="Decrease quantity"
                >
                  -
                </button>

                <div className="relative flex-1">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amountInputText}
                    onChange={(e) => handleTextChange(e.target.value)}
                    onBlur={handleBlur}
                    className="w-full h-12 px-3 text-center rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 text-base font-mono font-bold text-slate-900 dark:text-zinc-100 focus:outline-none focus:border-slate-400 dark:focus:border-zinc-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 dark:text-zinc-500 font-medium capitalize pointer-events-none">
                    {activeUnitDef.unit}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => handleStep(1)}
                  className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-lg transition-colors shadow-xs"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>

              {/* Calculated Grams for FODMAP Stacking */}
              <div className="mt-2 text-center text-xs font-mono font-medium text-slate-500 dark:text-zinc-400 bg-slate-100/70 dark:bg-zinc-900/60 py-1.5 px-2 rounded-lg">
                = {calculatedGrams}g (used for FODMAP stacking calculation)
              </div>
            </div>

            {/* Common Presets */}
            {portionConfig.presets.length > 0 && (
              <div>
                <label className="block text-[11px] text-slate-500 dark:text-zinc-400 mb-1.5 font-medium uppercase tracking-wider">
                  Standard & Common Presets
                </label>
                <div className="grid grid-cols-2 gap-1.5">
                  {portionConfig.presets.map((preset, pIdx) => {
                    const isPresetActive =
                      calculatedGrams === preset.grams ||
                      (amount === preset.amount && activeUnitDef.unit.toLowerCase() === preset.unit.toLowerCase());
                    return (
                      <button
                        key={pIdx}
                        type="button"
                        onClick={() => handleSetPreset(preset.amount, preset.unit)}
                        className={`min-h-[42px] px-2.5 py-1.5 rounded-xl text-xs font-medium text-left transition-colors flex items-center justify-between ${
                          isPresetActive
                            ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-sm'
                            : 'bg-slate-100 dark:bg-zinc-900 text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-800 border border-slate-200/50 dark:border-zinc-800'
                        }`}
                      >
                        <span className="truncate">{preset.label}</span>
                        {isPresetActive && <Check className="w-3.5 h-3.5 shrink-0 ml-1" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 min-h-[48px] rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-700 dark:text-zinc-300 text-xs font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => onSave(amount, activeUnitDef.unit, calculatedGrams)}
                className="flex-1 min-h-[48px] rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 text-xs font-semibold shadow-md transition-colors"
              >
                Save Portion ({calculatedGrams}g)
              </button>
            </div>
          </div>
        )}

        {/* VIEW 2: SWAP FOOD SEARCH */}
        {activeTab === 'swap' && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={swapSearchQuery}
                onChange={(e) => setSwapSearchQuery(e.target.value)}
                placeholder="Search replacement food..."
                className="w-full h-11 pl-9 pr-3 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-xs text-slate-900 dark:text-zinc-100 focus:outline-none focus:border-slate-400"
                autoFocus
              />
            </div>

            <div className="max-h-[280px] overflow-y-auto space-y-1.5 pr-1">
              {swapSearchResults.length === 0 ? (
                <div className="text-center py-6 text-xs text-slate-500 dark:text-zinc-400">
                  No matching foods found. You can add it in "+ Add to List".
                </div>
              ) : (
                swapSearchResults.map((food) => {
                  const ratingColor =
                    food.rating === 'low'
                      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
                      : food.rating === 'moderate'
                      ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30'
                      : 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30';

                  return (
                    <button
                      key={food.id}
                      type="button"
                      onClick={() => {
                        if (onSwapIngredient) {
                          onSwapIngredient(editingIngredient.dishIndex, editingIngredient.ingIndex, food);
                        }
                      }}
                      className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-zinc-900 hover:bg-slate-100 dark:hover:bg-zinc-800 border border-slate-200/60 dark:border-zinc-800 text-left transition-colors flex items-center justify-between"
                    >
                      <div className="truncate pr-2">
                        <div className="text-xs font-semibold text-slate-900 dark:text-zinc-100 truncate">
                          {food.name}
                        </div>
                        <div className="text-[10px] text-slate-500 dark:text-zinc-400 truncate">
                          {food.category} • {food.servings?.standard?.title || (food.servings?.low?.grams ? `Safe: ${food.servings.low.grams}g` : 'FODMAP Rated')}
                          {food.safeGrams && food.safeGrams < 500 && food.rating !== 'low' ? ` • Safe limit: ${food.safeGrams}g` : ''}
                        </div>
                      </div>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border shrink-0 ${ratingColor}`}>
                        {food.rating}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* VIEW 3: ADD TO BASE DATABASE */}
        {activeTab === 'addToBase' && (
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-medium text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wider">
                Food Name
              </label>
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                className="w-full h-10 px-3 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-xs font-semibold text-slate-900 dark:text-zinc-100 focus:outline-none focus:border-slate-400"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-medium text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wider">
                  Category
                </label>
                <select
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  className="w-full h-10 px-2.5 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-xs text-slate-900 dark:text-zinc-100 focus:outline-none"
                >
                  <option value="vegetables">Vegetables</option>
                  <option value="fruits">Fruits</option>
                  <option value="grains">Grains & Bread</option>
                  <option value="dairy">Dairy & Alts</option>
                  <option value="proteins">Proteins</option>
                  <option value="legumes">Legumes</option>
                  <option value="nuts">Nuts & Seeds</option>
                  <option value="oils-condiments">Oils & Condiments</option>
                  <option value="beverages">Beverages</option>
                  <option value="sweeteners-snacks">Sweeteners & Snacks</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wider">
                  Safe Grams
                </label>
                <input
                  type="number"
                  value={customSafeGrams}
                  onChange={(e) => setCustomSafeGrams(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-xs font-mono font-semibold text-slate-900 dark:text-zinc-100 focus:outline-none"
                  placeholder="50"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-medium text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wider">
                FODMAP Rating
              </label>
              <div className="grid grid-cols-3 gap-1.5">
                {(['low', 'moderate', 'high'] as FodmapRating[]).map((r) => {
                  const isSel = customRating === r;
                  const activeClass =
                    r === 'low'
                      ? 'bg-emerald-600 text-white'
                      : r === 'moderate'
                      ? 'bg-amber-600 text-white'
                      : 'bg-rose-600 text-white';
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setCustomRating(r)}
                      className={`h-9 rounded-xl text-xs font-bold uppercase transition-all ${
                        isSel ? activeClass : 'bg-slate-100 dark:bg-zinc-900 text-slate-600 dark:text-zinc-400'
                      }`}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-medium text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wider">
                Primary Culprit (Optional)
              </label>
              <select
                value={customCulprit}
                onChange={(e) => setCustomCulprit(e.target.value)}
                className="w-full h-10 px-2.5 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-xs text-slate-900 dark:text-zinc-100 focus:outline-none"
              >
                <option value="None">None / Safe</option>
                <option value="Fructans">Fructans</option>
                <option value="Lactose">Lactose</option>
                <option value="Excess Fructose">Excess Fructose</option>
                <option value="Sorbitol">Sorbitol</option>
                <option value="Mannitol">Mannitol</option>
                <option value="GOS">GOS</option>
              </select>
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={handleSaveToBase}
                className="w-full min-h-[46px] rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 text-xs font-semibold shadow-md transition-colors"
              >
                Save to FODMAP Database
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface FodmapSideScaleInspectorModalProps {
  item: MealIngredientItem;
  effectiveGrams: number;
  dishTitle?: string;
  onClose: () => void;
}

const FodmapSideScaleInspectorModal: React.FC<FodmapSideScaleInspectorModalProps> = ({
  item,
  effectiveGrams,
  dishTitle,
  onClose,
}) => {
  const sideScale = useMemo(() => {
    return calculateDetailedFodmapSideScale(item, effectiveGrams);
  }, [item, effectiveGrams]);

  const matched = useMemo(() => {
    return matchIngredientToFodmap(item.canonicalName || item.name).matchedFood;
  }, [item]);

  const safeBrackets = useMemo(() => {
    return getSafePortionBrackets(matched || item.name);
  }, [matched, item.name]);

  const subtypeIcons: Record<string, string> = {
    fructans: '🌾',
    gos: '🫘',
    fructose: '🍎',
    lactose: '🥛',
    sorbitol: '🥑',
    mannitol: '🍄',
  };

  const currentRating = sideScale.overallRating || item.rating || 'low';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/80 p-4 backdrop-blur-sm animate-in fade-in duration-150">
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-2xl p-5 space-y-4 shadow-2xl text-slate-900 dark:text-zinc-100">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-bold text-slate-900 dark:text-zinc-100 truncate">
                {item.name}
              </h3>
              <span
                className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase font-mono ${
                  currentRating === 'low'
                    ? 'bg-emerald-100 dark:bg-emerald-950/70 text-emerald-800 dark:text-emerald-200'
                    : currentRating === 'moderate'
                    ? 'bg-amber-100 dark:bg-amber-950/70 text-amber-800 dark:text-amber-200'
                    : 'bg-rose-100 dark:bg-rose-950/70 text-rose-800 dark:text-rose-200'
                }`}
              >
                {currentRating} FODMAP
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-zinc-400">
              {effectiveGrams}g ingested {dishTitle ? `• ${dishTitle}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close Inspector"
            className="w-8 h-8 rounded-full flex items-center justify-center bg-slate-100 dark:bg-zinc-800 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* 6-FODMAP Side Scale Meters */}
        <div className="p-4 rounded-xl bg-slate-50 dark:bg-zinc-900/80 border border-slate-200 dark:border-zinc-800 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:text-zinc-300">
              6-FODMAP Side Scale Analysis
            </div>
            <div className="text-[10px] text-slate-500 dark:text-zinc-400">
              Varney et al. (2017) Clinical Matrix
            </div>
          </div>

          <div className="space-y-2.5">
            {sideScale.items.map((it) => {
              const icon = subtypeIcons[it.id] || '🔬';
              const isHigh = it.rating === 'high';
              const isMod = it.rating === 'moderate';

              return (
                <div key={it.id} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-sm shrink-0">{icon}</span>
                      <span className="font-bold text-slate-800 dark:text-zinc-200 truncate">
                        {it.name}
                      </span>
                      <span className="text-[10px] text-slate-400 dark:text-zinc-500 hidden sm:inline">
                        ({it.chemicalName})
                      </span>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] font-mono text-slate-600 dark:text-zinc-400">
                        {it.grams}g <span className="opacity-60 text-[10px]">/ {it.cutoffGrams}g max</span>
                      </span>
                      <span
                        className={`px-1.5 py-0.2 rounded text-[9px] font-extrabold uppercase font-mono ${
                          isHigh
                            ? 'bg-rose-100 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300'
                            : isMod
                            ? 'bg-amber-100 dark:bg-amber-950/80 text-amber-700 dark:text-amber-300'
                            : 'bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300'
                        }`}
                      >
                        {it.rating}
                      </span>
                    </div>
                  </div>

                  {/* Side scale meter */}
                  <div className="h-1.5 w-full bg-slate-200/80 dark:bg-zinc-800 rounded-full overflow-hidden">
                    <div
                      style={{ width: `${Math.min(100, it.percentage)}%` }}
                      className={`h-full rounded-full transition-all duration-300 ${
                        isHigh
                          ? 'bg-rose-500'
                          : isMod
                          ? 'bg-amber-500'
                          : 'bg-emerald-500'
                      }`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Portion Safety & Clinical Limits */}
        <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-zinc-900/60 border border-slate-200 dark:border-zinc-800 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:text-zinc-300">
            Safe Portion Thresholds
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/40">
              <div className="text-[10px] font-bold uppercase text-emerald-800 dark:text-emerald-300">Safe</div>
              <div className="font-mono text-emerald-900 dark:text-emerald-200 text-[11px] font-semibold">{safeBrackets.safeRange}</div>
            </div>
            <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/40">
              <div className="text-[10px] font-bold uppercase text-amber-800 dark:text-amber-300">Moderate</div>
              <div className="font-mono text-amber-900 dark:text-amber-200 text-[11px] font-semibold">{safeBrackets.moderateRange}</div>
            </div>
            <div className="p-2 rounded-lg bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/40">
              <div className="text-[10px] font-bold uppercase text-rose-800 dark:text-rose-300">High</div>
              <div className="font-mono text-rose-900 dark:text-rose-200 text-[11px] font-semibold">{safeBrackets.highRange}</div>
            </div>
          </div>
          {item.servingLimitNote && (
            <p className="text-[11px] text-slate-600 dark:text-zinc-400 italic pt-1">
              💡 {item.servingLimitNote}
            </p>
          )}
        </div>

        {/* Low-FODMAP Swaps */}
        {(item.swapSuggestion || matched?.swapSuggestion) && (
          <div className="p-3.5 rounded-xl bg-indigo-50/70 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800/50 space-y-1">
            <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-800 dark:text-indigo-300">
              Certified Low-FODMAP Swap
            </div>
            <p className="text-xs text-indigo-900 dark:text-indigo-200 font-medium">
              👉 {item.swapSuggestion || matched?.swapSuggestion}
            </p>
          </div>
        )}

        <button
          onClick={onClose}
          className="w-full min-h-[44px] rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 text-xs font-semibold transition-colors shadow-md"
        >
          Done
        </button>
      </div>
    </div>
  );
};

interface DishTotalWeightModalProps {
  dishTitle: string;
  currentGrams: number;
  onClose: () => void;
  onSave: (targetGrams: number) => void;
}

const DishTotalWeightModal: React.FC<DishTotalWeightModalProps> = ({
  dishTitle,
  currentGrams,
  onClose,
  onSave,
}) => {
  const [targetGrams, setTargetGrams] = useState<number>(currentGrams || 240);
  const [textVal, setTextVal] = useState<string>(String(currentGrams || 240));

  const presets = [180, 240, 280, 350, 400, 500];

  const handleSelectPreset = (g: number) => {
    setTargetGrams(g);
    setTextVal(String(g));
  };

  const handleChangeText = (v: string) => {
    setTextVal(v);
    const num = parseFloat(v);
    if (!isNaN(num) && num > 0) {
      setTargetGrams(num);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/80 p-4 backdrop-blur-sm animate-in fade-in duration-150">
      <div className="w-full max-w-sm bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-2xl p-5 space-y-4 shadow-2xl text-slate-900 dark:text-zinc-100">
        <div className="flex items-center justify-between">
          <div className="truncate pr-2">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-zinc-100 truncate">
              Scale Dish Net Weight
            </h3>
            <p className="text-xs text-slate-500 dark:text-zinc-400 truncate">
              {dishTitle || 'Dish'}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-full flex items-center justify-center bg-slate-100 dark:bg-zinc-800 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-slate-600 dark:text-zinc-400">
          Proportionally scales all ingredients and updates FODMAP chemical levels based on the package net weight (e.g. printed &quot;240g e&quot;).
        </p>

        {/* Input */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-zinc-400">
            Total Pack / Dish Weight (grams)
          </label>
          <div className="relative">
            <input
              type="number"
              min="10"
              max="2500"
              step="5"
              value={textVal}
              onChange={(e) => handleChangeText(e.target.value)}
              className="w-full h-11 pl-4 pr-12 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-300 dark:border-zinc-700 text-base font-bold font-mono text-slate-900 dark:text-zinc-100 focus:outline-none focus:border-slate-500"
            />
            <span className="absolute right-4 top-3 text-xs font-bold text-slate-400 dark:text-zinc-500">
              g
            </span>
          </div>
        </div>

        {/* Quick Presets */}
        <div className="space-y-1.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400">
            Quick Pack Presets:
          </div>
          <div className="grid grid-cols-3 gap-2">
            {presets.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => handleSelectPreset(g)}
                className={`py-2 px-2.5 rounded-xl text-xs font-bold font-mono transition-all ${
                  targetGrams === g
                    ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-sm'
                    : 'bg-slate-100 hover:bg-slate-200 dark:bg-zinc-900 dark:hover:bg-zinc-800 text-slate-700 dark:text-zinc-300 border border-slate-200 dark:border-zinc-800'
                }`}
              >
                {g}g
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 min-h-[44px] rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-700 dark:text-zinc-300 text-xs font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (targetGrams > 0) {
                onSave(targetGrams);
              }
            }}
            className="flex-1 min-h-[44px] rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 text-xs font-semibold transition-colors shadow-md"
          >
            Apply Scale
          </button>
        </div>
      </div>
    </div>
  );
};

interface DishPortionModalProps {
  dishTitle: string;
  currentMultiplier: number;
  recipeServings?: number;
  portionsEaten?: number;
  dishGramsTotal?: number;
  onClose: () => void;
  onSave: (finalMultiplier: number, recipeServings?: number, portionsEaten?: number) => void;
}

const DishPortionModal: React.FC<DishPortionModalProps> = ({
  dishTitle,
  currentMultiplier,
  recipeServings: initServings,
  portionsEaten: initEaten,
  dishGramsTotal = 0,
  onClose,
  onSave,
}) => {
  const [tab, setTab] = useState<'yield' | 'direct'>(() => {
    if (initServings && initServings > 1) return 'yield';
    if (currentMultiplier !== 1.0 && currentMultiplier < 0.5) return 'yield';
    return 'yield';
  });

  // Yield mode state: e.g. Serves 4, ate 1 portion
  const [servings, setServings] = useState<number>(() => {
    if (initServings && initServings > 0) return initServings;
    if (currentMultiplier < 1 && currentMultiplier > 0) {
      const approx = Math.round(1 / currentMultiplier);
      return approx > 1 ? approx : 4;
    }
    return 4;
  });

  const [eaten, setEaten] = useState<number>(() => {
    if (initEaten && initEaten > 0) return initEaten;
    return 1;
  });

  // Direct multiplier state
  const [directMult, setDirectMult] = useState<number>(currentMultiplier || 1.0);
  const [directInputText, setDirectInputText] = useState<string>(String(currentMultiplier || 1.0));

  // Computed multiplier based on active tab
  const computedMultiplier = useMemo(() => {
    if (tab === 'yield') {
      const s = Math.max(1, servings);
      const e = Math.max(0.05, eaten);
      return Math.round((e / s) * 1000) / 1000;
    }
    return directMult;
  }, [tab, servings, eaten, directMult]);

  const portionGrams = useMemo(() => {
    return Math.round(dishGramsTotal * computedMultiplier);
  }, [dishGramsTotal, computedMultiplier]);

  const handleApply = () => {
    if (tab === 'yield') {
      onSave(computedMultiplier, servings > 1 ? servings : undefined, servings > 1 ? eaten : undefined);
    } else {
      onSave(directMult, undefined, undefined);
    }
  };

  const handleStepServings = (delta: number) => {
    setServings((prev) => Math.max(1, Math.min(50, prev + delta)));
  };

  const handleStepEaten = (delta: number) => {
    setEaten((prev) => Math.max(0.25, Math.round((prev + delta) * 100) / 100));
  };

  const handleSelectPresetMultiplier = (m: number) => {
    setDirectMult(m);
    setDirectInputText(String(m));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/75 p-4 backdrop-blur-sm animate-in fade-in duration-150">
      <div className="w-full max-w-sm max-h-[90vh] overflow-y-auto bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-2xl p-5 space-y-4 shadow-2xl text-slate-900 dark:text-zinc-100">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="truncate pr-2">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-zinc-100 truncate">
              {dishTitle || 'Dish Portion'}
            </h3>
            <p className="text-xs text-slate-500 dark:text-zinc-400">
              Recipe yield & portion size calculator
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 dark:text-zinc-400 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex rounded-xl bg-slate-100 dark:bg-zinc-900 p-1 border border-slate-200 dark:border-zinc-800">
          <button
            type="button"
            onClick={() => setTab('yield')}
            className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
              tab === 'yield'
                ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-xs'
                : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            Recipe Yield & Servings
          </button>
          <button
            type="button"
            onClick={() => setTab('direct')}
            className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
              tab === 'direct'
                ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-xs'
                : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            Direct Multiplier
          </button>
        </div>

        {/* Live Calculation Banner with Visual Portion Meter */}
        <div className="p-3.5 rounded-xl bg-slate-900 text-white dark:bg-zinc-900 border border-slate-800 dark:border-zinc-800 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300 dark:text-zinc-400">
              Your Portion Scale
            </span>
            <span className="text-xs font-mono font-bold text-emerald-400">
              {computedMultiplier}x ({Math.round(computedMultiplier * 100)}% of dish)
            </span>
          </div>
          {/* Visual progress bar */}
          <div className="w-full h-2 rounded-full bg-slate-800 dark:bg-zinc-800 overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${Math.min(100, Math.max(4, Math.round(computedMultiplier * 100)))}%` }}
            />
          </div>
          <div className="text-xs text-slate-200 dark:text-zinc-300 font-medium flex items-center justify-between pt-0.5">
            {tab === 'yield' ? (
              <span>
                Eating <strong>{eaten}</strong> of <strong>{servings}</strong> {servings === 1 ? 'serving' : 'servings'}
              </span>
            ) : (
              <span>Direct scale factor: <strong>{directMult}x</strong></span>
            )}
            {dishGramsTotal > 0 && (
              <span className="text-slate-400 text-[11px]">
                ~{portionGrams}g of {Math.round(dishGramsTotal)}g
              </span>
            )}
          </div>
        </div>

        {tab === 'yield' ? (
          <div className="space-y-4">
            {/* Step 1: Recipe Servings */}
            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-zinc-300">
                1. How many people does this recipe serve?
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleStepServings(-1)}
                  className="w-11 h-11 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-base transition-colors"
                >
                  -
                </button>
                <div className="relative flex-1">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={servings}
                    onChange={(e) => setServings(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full h-11 px-3 text-center rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 text-sm font-mono font-bold text-slate-900 dark:text-zinc-100 focus:outline-none focus:border-slate-400"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 dark:text-zinc-500 font-medium pointer-events-none">
                    {servings === 1 ? 'serving' : 'servings'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleStepServings(1)}
                  className="w-11 h-11 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-base transition-colors"
                >
                  +
                </button>
              </div>

              {/* Streamlined Yield Pills */}
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {[1, 2, 4, 6, 8].map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setServings(num)}
                    className={`flex-1 min-w-[50px] py-1.5 rounded-lg text-xs font-semibold text-center transition-all ${
                      servings === num
                        ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-xs'
                        : 'bg-slate-100 dark:bg-zinc-900 text-slate-600 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-800'
                    }`}
                  >
                    Serves {num}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Portions You Ate */}
            <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-zinc-800/80">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-zinc-300">
                2. How much did you eat?
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleStepEaten(-0.25)}
                  className="w-11 h-11 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-base transition-colors"
                >
                  -
                </button>
                <div className="relative flex-1">
                  <input
                    type="number"
                    step="0.25"
                    min="0.1"
                    max="20"
                    value={eaten}
                    onChange={(e) => setEaten(Math.max(0.1, parseFloat(e.target.value) || 1))}
                    className="w-full h-11 px-3 text-center rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 text-sm font-mono font-bold text-slate-900 dark:text-zinc-100 focus:outline-none focus:border-slate-400"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 dark:text-zinc-500 font-medium pointer-events-none">
                    {eaten === 1 ? 'portion' : 'portions'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleStepEaten(0.25)}
                  className="w-11 h-11 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-base transition-colors"
                >
                  +
                </button>
              </div>

              {/* Streamlined Eaten Pills */}
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {[
                  { label: '0.5 portion', val: 0.5 },
                  { label: '1 portion', val: 1 },
                  { label: '1.5 portions', val: 1.5 },
                  { label: '2 portions', val: 2 },
                  { label: `All (${servings})`, val: servings },
                ].map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => setEaten(item.val)}
                    className={`flex-1 min-w-[56px] py-1.5 rounded-lg text-xs font-semibold text-center transition-all ${
                      eaten === item.val
                        ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-xs'
                        : 'bg-slate-100 dark:bg-zinc-900 text-slate-600 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-800'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Direct Multiplier Presets */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-zinc-300 mb-2">
                Quick Multipliers & Fractions
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: '0.125x (1/8)', val: 0.125 },
                  { label: '0.25x (1/4)', val: 0.25 },
                  { label: '0.33x (1/3)', val: 0.333 },
                  { label: '0.5x (1/2)', val: 0.5 },
                  { label: '0.75x (3/4)', val: 0.75 },
                  { label: '1.0x (Full)', val: 1.0 },
                  { label: '1.25x', val: 1.25 },
                  { label: '1.5x', val: 1.5 },
                  { label: '2.0x (Double)', val: 2.0 },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => handleSelectPresetMultiplier(preset.val)}
                    className={`min-h-[44px] rounded-xl text-xs font-semibold transition-colors ${
                      directMult === preset.val
                        ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-sm'
                        : 'bg-slate-50 dark:bg-zinc-900 text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800 border border-slate-200 dark:border-zinc-800'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Direct Multiplier Input */}
            <div className="space-y-1.5 pt-2 border-t border-slate-100 dark:border-zinc-800/80">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-zinc-300">
                Custom Multiplier (Decimal)
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const next = Math.max(0.05, Math.round((directMult - 0.1) * 100) / 100);
                    setDirectMult(next);
                    setDirectInputText(String(next));
                  }}
                  className="w-11 h-11 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-base transition-colors"
                >
                  -
                </button>
                <div className="relative flex-1">
                  <input
                    type="number"
                    step="0.05"
                    min="0.01"
                    max="20"
                    value={directInputText}
                    onChange={(e) => {
                      setDirectInputText(e.target.value);
                      const parsed = parseFloat(e.target.value);
                      if (!isNaN(parsed) && parsed > 0) {
                        setDirectMult(parsed);
                      }
                    }}
                    className="w-full h-11 px-3 text-center rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 text-sm font-mono font-bold text-slate-900 dark:text-zinc-100 focus:outline-none focus:border-slate-400"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 dark:text-zinc-500 font-mono font-bold pointer-events-none">
                    x
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const next = Math.round((directMult + 0.1) * 100) / 100;
                    setDirectMult(next);
                    setDirectInputText(String(next));
                  }}
                  className="w-11 h-11 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-base transition-colors"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2 border-t border-slate-100 dark:border-zinc-800">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 min-h-[46px] rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-700 dark:text-zinc-300 font-semibold text-xs transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="flex-1 min-h-[46px] rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 font-bold text-xs tracking-wide transition-colors shadow-sm"
          >
            Apply ({computedMultiplier}x)
          </button>
        </div>
      </div>
    </div>
  );
};

export const UnifiedMealLoggerModal: React.FC<UnifiedMealLoggerModalProps> = ({
  isOpen,
  onClose,
  onSaveMeal,
  onDeleteMeal,
  initialMeal,
  initialTab = 'search',
  defaultMealType,
  recentMeals: recentMealsProp,
}) => {
  // Time and Date State
  const now = useMemo(() => new Date(), []);
  const [entryDate, setEntryDate] = useState(() =>
    initialMeal
      ? extractLocalDateFromIso(initialMeal.timestamp_iso || initialMeal.date)
      : formatIsoDate(now)
  );
  const [entryTime, setEntryTime] = useState(() =>
    initialMeal
      ? extractLocalTimeFromIso(initialMeal.timestamp_iso)
      : `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
  );

  const [mealCategory, setMealCategory] = useState<MealType>(
    () => initialMeal?.meal_type || defaultMealType || getDefaultMealType()
  );

  const handleTimeChange = (newTime: string) => {
    setEntryTime(newTime);
    const autoCategory = getDefaultMealType(newTime);
    setMealCategory(autoCategory);
  };
  const [activeTab, setActiveTab] = useState<TabMode>(() => {
    const hasExistingMeal = Boolean(
      initialMeal &&
        ((initialMeal.dishes && initialMeal.dishes.length > 0) ||
          (initialMeal.ingredients && initialMeal.ingredients.length > 0) ||
          initialMeal.ingredients_tags ||
          initialMeal.description)
    );
    if (hasExistingMeal) return null;
    return initialTab ?? 'search';
  });
  const [scanSubTab, setScanSubTab] = useState<ScanSubTab>('barcode');
  const [aiSubTab, setAiSubTab] = useState<AiSubTab>('snap');
  const [isFodmapEnabled, setIsFodmapEnabled] = useState<boolean>(() => localStore.getFodmapTrackingEnabled());

  useEffect(() => {
    const handler = (e: any) => {
      if (e?.detail?.enabled !== undefined) {
        setIsFodmapEnabled(e.detail.enabled);
      } else {
        setIsFodmapEnabled(localStore.getFodmapTrackingEnabled());
      }
    };
    window.addEventListener('digestlog_fodmap_toggle', handler);
    return () => window.removeEventListener('digestlog_fodmap_toggle', handler);
  }, []);

  // Multi-Dish Plate State
  const [dishes, setDishes] = useState<PlateDish[]>(() => {
    if (!initialMeal) {
      return [{
        id: `dish-${Date.now()}-1`,
        title: 'MAIN DISH',
        portionMultiplier: 1.0,
        ingredients: [],
      }];
    }
    if (initialMeal.dishes && initialMeal.dishes.length > 0) {
      return initialMeal.dishes;
    }
    const ings = initialMeal.ingredients && initialMeal.ingredients.length > 0
      ? initialMeal.ingredients
      : convertTagsToIngredientItems(initialMeal.ingredients_tags || initialMeal.description || 'Meal');
    return [{
      id: `dish-${Date.now()}-1`,
      title: (initialMeal.description || 'MAIN MEAL').toUpperCase(),
      portionMultiplier: initialMeal.portionMultiplier || 1.0,
      ingredients: ings,
      nutrition: initialMeal.nutrition,
    }];
  });
  const [activeDishIndex, setActiveDishIndex] = useState<number>(0);
  const [collapsedDishes, setCollapsedDishes] = useState<Record<string, boolean>>({});
  const [expandedSauceIds, setExpandedSauceIds] = useState<Set<string>>(new Set());

  const toggleSauceExpanded = (id: string) => {
    setExpandedSauceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Clinical Cards State
  const [confirmedFreeFrom, setConfirmedFreeFrom] = useState<string[]>(() => {
    if (initialMeal?.anti_aggravates) {
      return initialMeal.anti_aggravates
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  });
  const [notes, setNotes] = useState(() => initialMeal?.notes || '');

  // Modals & Sheets State
  const [showStackingDetail, setShowStackingDetail] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [showMealSettings, setShowMealSettings] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingDishIndex, setEditingDishIndex] = useState<number | null>(null);
  const [editDishName, setEditDishName] = useState('');
  const [inlineIngInputs, setInlineIngInputs] = useState<Record<number, string>>({});
  const [focusedDishIdx, setFocusedDishIdx] = useState<number | null>(null);
  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  // Portion edit state for ingredient
  const [editingIngredient, setEditingIngredient] = useState<{
    dishIndex: number;
    ingIndex: number;
    id?: string;
    name: string;
    amount: number;
    unit: string;
    gramsEstimate: number;
    servingLimitNote?: string;
    rating?: FodmapRating;
    primaryCulprit?: string;
  } | null>(null);

  // Portion multiplier edit for dish
  const [editingPortionDish, setEditingPortionDish] = useState<{
    dishIndex: number;
    currentMultiplier: number;
    recipeServings?: number;
    portionsEaten?: number;
  } | null>(null);

  // 6-FODMAP Side Scale Inspector Modal State
  const [inspectingIngredientItem, setInspectingIngredientItem] = useState<{
    item: MealIngredientItem;
    effectiveGrams: number;
    dishTitle?: string;
  } | null>(null);

  // Scale Total Dish Net Weight Modal State
  const [editingDishWeight, setEditingDishWeight] = useState<{
    dishIndex: number;
    currentGrams: number;
    dishTitle: string;
  } | null>(null);

  // Search Tab State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FodmapFoodItem[]>([]);
  const [offResults, setOffResults] = useState<OpenFoodFactsProductResult[]>([]);
  const [isSearchingOff, setIsSearchingOff] = useState(false);
  const offDebounceRef = useRef<any>(null);
  const [recentMeals, setRecentMeals] = useState<Meal[]>(() => recentMealsProp || []);

  // AI State & Recipe Servings
  const [aiText, setAiText] = useState('');
  const [aiRecipeServings, setAiRecipeServings] = useState<number>(1);
  const [aiPortionsEaten, setAiPortionsEaten] = useState<number>(1);
  const [aiIsListening, setAiIsListening] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiStatusMessage, setAiStatusMessage] = useState<string | null>(null);
  const [platePhotoPreview, setPlatePhotoPreview] = useState<string | null>(null);
  const [workspaceScrollY, setWorkspaceScrollY] = useState(0);



  // Barcode / Scanner State
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scannerStatus, setScannerStatus] = useState<string | null>(null);

  const lookupBarcode = useCallback(async (barcode: string) => {
    const clean = barcode.trim();
    if (!clean) return;
    setScannerStatus(`Looking up ${clean} in OpenFoodFacts...`);

    try {
      const product = await fetchProductByBarcode(clean);
      if (product && product.name) {
        const ingredients = product.ingredients || [product.name];
        const newItems = convertTagsToIngredientItems(ingredients);
        const newDish: PlateDish = {
          id: `dish-${Date.now()}`,
          title: product.name.toUpperCase(),
          portionMultiplier: 1.0,
          ingredients: newItems,
        };
        setDishes((prev) => (prev.length === 1 && prev[0].ingredients.length === 0 ? [newDish] : [...prev, newDish]));
        setScannerStatus(null);
        setBarcodeInput('');
        setActiveTab(null);
      } else {
        setScannerStatus(`No product found for barcode ${clean}.`);
      }
    } catch {
      setScannerStatus(`Failed to lookup barcode ${clean}.`);
    }
  }, []);

  const scannerViewfinderRef = useRef<CameraViewfinderRef>(null);
  const aiCameraViewfinderRef = useRef<CameraViewfinderRef>(null);

  const {
    isScanning: isScanningBarcode,
    startScanningVideo,
    stopCamera: stopLiveBarcodeScanner,
    decodeFromPhoto: decodeBarcodeFromPhoto,
  } = useBarcodeScanner({
    onDetected: (detectedCode) => {
      lookupBarcode(detectedCode);
    },
  });

  // File Inputs
  const cameraFileInputRef = useRef<HTMLInputElement>(null);
  const galleryFileInputRef = useRef<HTMLInputElement>(null);
  const labelFileInputRef = useRef<HTMLInputElement>(null);
  const labelGalleryInputRef = useRef<HTMLInputElement>(null);
  const barcodePhotoInputRef = useRef<HTMLInputElement>(null);

  // Saved Recipes Library
  const [savedRecipes, setSavedRecipes] = useState<SavedRecipe[]>(() => {
    try {
      return localStore.getSavedRecipes() || [];
    } catch {
      return [];
    }
  });

  const lastProcessedMealIdRef = useRef<string | null>(initialMeal?.id || (initialMeal ? 'draft-initial' : null));

  // -------------------------------------------------------------
  // Initialization & Dynamic Prop Syncing
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;

    initializeFodmapDatabase().catch(() => {});

    // If initialMeal ID hasn't changed since useState mounted this instance, skip redundant resetting
    const currentMealId = initialMeal?.id || (initialMeal ? 'draft-initial' : null);
    if (lastProcessedMealIdRef.current === currentMealId) {
      return;
    }
    lastProcessedMealIdRef.current = currentMealId;

    if (!recentMealsProp || recentMealsProp.length === 0) {
      setRecentMeals(localStore.getMeals());
    } else {
      setRecentMeals(recentMealsProp);
    }
    setSavedRecipes(localStore.getSavedRecipes());

    const hasExistingMeal = Boolean(
      initialMeal &&
        ((initialMeal.dishes && initialMeal.dishes.length > 0) ||
          (initialMeal.ingredients && initialMeal.ingredients.length > 0) ||
          initialMeal.ingredients_tags ||
          initialMeal.description)
    );

    // If a meal is already added or being edited, deselect the menu adding options (activeTab = null)
    if (hasExistingMeal) {
      setActiveTab(null);
    } else {
      setActiveTab(initialTab ?? 'search');
    }

    if (initialMeal) {
      setEntryDate(extractLocalDateFromIso(initialMeal.timestamp_iso || initialMeal.date));
      setEntryTime(extractLocalTimeFromIso(initialMeal.timestamp_iso));

      setMealCategory(initialMeal.meal_type || getDefaultMealType());
      setNotes(initialMeal.notes || '');
      if (initialMeal.anti_aggravates) {
        setConfirmedFreeFrom(
          initialMeal.anti_aggravates
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        );
      }

      if (initialMeal.dishes && initialMeal.dishes.length > 0) {
        setDishes(initialMeal.dishes);
      } else {
        const ings = initialMeal.ingredients && initialMeal.ingredients.length > 0
          ? initialMeal.ingredients
          : convertTagsToIngredientItems(
              initialMeal.ingredients_tags || initialMeal.description || 'Meal'
            );
        const singleDish: PlateDish = {
          id: `dish-${Date.now()}-1`,
          title: (initialMeal.description || 'MAIN MEAL').toUpperCase(),
          portionMultiplier: initialMeal.portionMultiplier || 1.0,
          ingredients: ings,
          nutrition: initialMeal.nutrition,
        };
        setDishes([singleDish]);
      }
    } else {
      const cur = new Date();
      setEntryDate(formatIsoDate(cur));
      const hh = String(cur.getHours()).padStart(2, '0');
      const mm = String(cur.getMinutes()).padStart(2, '0');
      const currentFormattedTime = `${hh}:${mm}`;
      setEntryTime(currentFormattedTime);
      setMealCategory(defaultMealType || getDefaultMealType(currentFormattedTime));

      const defaultDish: PlateDish = {
        id: `dish-${Date.now()}-1`,
        title: 'MAIN DISH',
        portionMultiplier: 1.0,
        ingredients: [],
      };
      setDishes([defaultDish]);
      setActiveDishIndex(0);
      setConfirmedFreeFrom([]);
      setNotes('');
      setPlatePhotoPreview(null);
    }
  }, [isOpen, initialMeal, defaultMealType]);

  // Stop all camera streams
  const stopAllCameras = useCallback(() => {
    stopLiveBarcodeScanner();
  }, [stopLiveBarcodeScanner]);

  // Clean up on unmount or close
  useEffect(() => {
    if (!isOpen) {
      stopAllCameras();
    }
    return () => {
      stopAllCameras();
    };
  }, [isOpen, stopAllCameras]);

  // Dynamic Search Query (FODMAP local 0ms + Open Food Facts Debounced)
  useEffect(() => {
    const clean = searchQuery.trim();
    if (!clean) {
      setSearchResults([]);
      setOffResults([]);
      setIsSearchingOff(false);
      if (offDebounceRef.current) clearTimeout(offDebounceRef.current);
      return;
    }
    const results = searchFodmapDatabase(clean);
    setSearchResults(results.slice(0, 15));

    if (clean.length >= 2) {
      setIsSearchingOff(true);
      if (offDebounceRef.current) clearTimeout(offDebounceRef.current);
      offDebounceRef.current = setTimeout(async () => {
        try {
          const off = await searchOpenFoodFacts(clean, 8);
          setOffResults(off);
        } catch {
          setOffResults([]);
        } finally {
          setIsSearchingOff(false);
        }
      }, 200);
    } else {
      setOffResults([]);
      setIsSearchingOff(false);
    }

    return () => {
      if (offDebounceRef.current) clearTimeout(offDebounceRef.current);
    };
  }, [searchQuery]);

  // Recalculate Aggregated Plate Nutrition
  const plateNutrition = useMemo(() => {
    let totalCals = 0;
    let totalP = 0;
    let totalF = 0;
    let totalC = 0;
    let totalGrams = 0;

    for (const dish of dishes) {
      const mult = dish.portionMultiplier || 1.0;
      for (const ing of dish.ingredients) {
        totalGrams += (ing.gramsEstimate || 0) * mult;
        totalCals += (ing.calories || 0) * mult;
        totalP += (ing.protein || 0) * mult;
        totalF += (ing.fat || 0) * mult;
        totalC += (ing.carbs || 0) * mult;
      }
    }

    return {
      calories: Math.round(totalCals),
      protein: Math.round(totalP * 10) / 10,
      fat: Math.round(totalF * 10) / 10,
      carbs: Math.round(totalC * 10) / 10,
      totalGrams: Math.round(totalGrams),
    };
  }, [dishes]);

  // Plate FODMAP Stacking Report
  const stackingReport = useMemo<DetailedFodmapStackingReport>(() => {
    const timestamp = createIsoFromLocalDateAndTime(entryDate, entryTime);
    return calculatePlateFodmapStacking(dishes, recentMeals, timestamp);
  }, [dishes, recentMeals, entryDate, entryTime]);

  // Check if plate has any ingredients logged
  const hasPlateItems = useMemo(() => {
    return dishes.some((d) => d.ingredients && d.ingredients.length > 0);
  }, [dishes]);

  // Tab click toggle helper: clicking current open tab toggles it closed (null), else opens & auto-starts camera
  const handleTabClick = (tab: 'search' | 'scan' | 'ai' | 'library') => {
    if (activeTab === tab) {
      stopAllCameras();
      setActiveTab(null);
    } else {
      stopAllCameras();
      setActiveTab(tab);
      if (tab === 'scan') {
        setScanSubTab('barcode');
      }
    }
  };

  const handleSwitchAiSubTab = (subTab: 'snap' | 'describe') => {
    setAiSubTab(subTab);
  };

  // -------------------------------------------------------------
  // Multi-Dish Management Helpers
  // -------------------------------------------------------------
  const ensureActiveDish = (): number => {
    if (dishes.length === 0) {
      const newDish: PlateDish = {
        id: `dish-${Date.now()}-1`,
        title: 'MAIN DISH',
        portionMultiplier: 1.0,
        ingredients: [],
      };
      setDishes([newDish]);
      setActiveDishIndex(0);
      return 0;
    }
    const idx = Math.min(Math.max(0, activeDishIndex), dishes.length - 1);
    return idx;
  };

  const handleAddNewDishToPlate = (customTitle?: string) => {
    const newDish: PlateDish = {
      id: `dish-${Date.now()}-${dishes.length + 1}`,
      title: (customTitle || `FOOD ${dishes.length + 1}`).toUpperCase(),
      portionMultiplier: 1.0,
      ingredients: [],
    };
    setDishes((prev) => [...prev, newDish]);
    setActiveDishIndex(dishes.length);
  };

  const handleRemoveDish = (index: number) => {
    if (dishes.length <= 1) {
      // Clear ingredients instead of deleting last dish
      setDishes([{ ...dishes[0], ingredients: [] }]);
      return;
    }
    setDishes((prev) => prev.filter((_, i) => i !== index));
    setActiveDishIndex(0);
  };

  const handleAddDirectFoodItem = (dishIndex: number, food: FodmapFoodItem) => {
    const realistic = getRealisticCulinaryServing(food.name, food);
    const rawGrams = realistic.grams || food.servings?.low?.grams || 50;
    const defaultGrams = rawGrams >= 400 ? (realistic.grams && realistic.grams < 400 ? realistic.grams : 100) : rawGrams;
    const safeNote = food.servings?.low?.title ? `Safe limit: ${food.servings.low.title}` : realistic.servingLimitNote;

    const newItem: MealIngredientItem = {
      id: `ing-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: food.name,
      canonicalName: food.name,
      amount: realistic.amount || (realistic.unit === 'g' || realistic.unit === 'ml' ? defaultGrams : 1),
      unit: realistic.unit || 'g',
      gramsEstimate: defaultGrams,
      rating: food.rating,
      primaryCulprit: food.rating === 'high' || food.rating === 'moderate' ? (food.swapSuggestion ? 'Fructans' : undefined) : undefined,
      servingLimitNote: safeNote,
      fodmaps: food.fodmaps,
      isCertifiedSwap: food.rating === 'low',
    };

    setDishes((prev) => {
      const copy = [...prev];
      const targetDish = { ...copy[dishIndex] };
      const updatedIngredients = [...targetDish.ingredients, newItem];
      targetDish.ingredients = updatedIngredients;
      if (!targetDish.isCustomTitle) {
        const autoTitle = synthesizeDishTitle(updatedIngredients);
        if (autoTitle) {
          targetDish.title = autoTitle.toUpperCase();
        }
      }
      copy[dishIndex] = targetDish;
      return copy;
    });
  };

  const handleAddIngredientToDish = (dishIndex: number, ingredientName: string) => {
    const clean = (ingredientName || '').trim();
    if (!clean) return;

    const newItems = convertTagsToIngredientItems([clean]);
    if (newItems.length === 0) return;

    setDishes((prev) => {
      const copy = [...prev];
      const targetDish = { ...copy[dishIndex] };
      const updatedIngredients = [...targetDish.ingredients, newItems[0]];
      targetDish.ingredients = updatedIngredients;
      if (!targetDish.isCustomTitle) {
        const autoTitle = synthesizeDishTitle(updatedIngredients);
        if (autoTitle) {
          targetDish.title = autoTitle.toUpperCase();
        }
      }
      copy[dishIndex] = targetDish;
      return copy;
    });
  };

  const handleRemoveIngredient = (dishIndex: number, ingIndex: number) => {
    setDishes((prev) => {
      const copy = [...prev];
      const targetDish = { ...copy[dishIndex] };
      const updatedIngredients = targetDish.ingredients.filter((_, idx) => idx !== ingIndex);
      targetDish.ingredients = updatedIngredients;
      if (!targetDish.isCustomTitle) {
        targetDish.title = updatedIngredients.length > 0
          ? (synthesizeDishTitle(updatedIngredients).toUpperCase() || 'MAIN DISH')
          : 'MAIN DISH';
      }
      copy[dishIndex] = targetDish;
      return copy;
    });
  };

  const toggleDishCollapse = (dishId: string) => {
    setCollapsedDishes((prev) => ({
      ...prev,
      [dishId]: !prev[dishId],
    }));
  };

  // -------------------------------------------------------------
  // Add from Search, History & Library
  // -------------------------------------------------------------
  const handleSelectSearchResult = (food: FodmapFoodItem) => {
    const targetIdx = ensureActiveDish();
    handleAddDirectFoodItem(targetIdx, food);
    setSearchQuery('');
    setOffResults([]);
    setActiveTab(null);
  };

  const handleAddOffProductToPlate = (
    product: OpenFoodFactsProductResult,
    asNewDish: boolean = false
  ) => {
    const rawList =
      product.ingredients && product.ingredients.length > 0
        ? product.ingredients
        : [product.name];

    const convertedItems = convertTagsToIngredientItems(rawList);
    const finalItems: MealIngredientItem[] =
      convertedItems.length > 0
        ? convertedItems
        : [
            {
              id: `ing-${Date.now()}`,
              name: product.name,
              canonicalName: canonicalizeIngredient(product.name) || product.name.toLowerCase(),
              amount: 1,
              unit: 'serving',
              gramsEstimate: 100,
              rating: 'low' as FodmapRating,
            },
          ];

    if (asNewDish) {
      const newDish: PlateDish = {
        id: `dish-${Date.now()}-${dishes.length + 1}`,
        title: product.name.toUpperCase(),
        portionMultiplier: 1.0,
        ingredients: finalItems,
      };
      setDishes((prev) => {
        if (prev.length === 1 && prev[0].title === 'MAIN DISH' && prev[0].ingredients.length === 0) {
          return [newDish];
        }
        return [...prev, newDish];
      });
      setActiveDishIndex(dishes.length);
    } else {
      const targetIdx = ensureActiveDish();
      setDishes((prev) => {
        const next = [...prev];
        const targetDish = { ...next[targetIdx] };
        targetDish.ingredients = [...targetDish.ingredients, ...finalItems];
        next[targetIdx] = targetDish;
        return next;
      });
    }

    setSearchQuery('');
    setOffResults([]);
    setActiveTab(null);
  };

  const handleAddCustomMealAsNewDish = (dishTitle: string) => {
    const clean = (dishTitle || '').trim();
    if (!clean) return;
    const newItems = convertTagsToIngredientItems(clean);
    const fallbackItem: MealIngredientItem = {
      id: `ing-${Date.now()}`,
      name: clean,
      canonicalName: clean.toLowerCase(),
      amount: 1,
      unit: 'serving',
      gramsEstimate: 150,
      rating: 'low',
    };
    const newDish: PlateDish = {
      id: `dish-${Date.now()}-${dishes.length + 1}`,
      title: clean.toUpperCase(),
      portionMultiplier: 1.0,
      ingredients: newItems.length > 0 ? newItems : [fallbackItem],
    };
    setDishes((prev) => {
      if (prev.length === 1 && prev[0].title === 'MAIN DISH' && prev[0].ingredients.length === 0) {
        return [newDish];
      }
      return [...prev, newDish];
    });
    setActiveDishIndex(dishes.length);
    setSearchQuery('');
    setActiveTab(null);
  };

  const handleAddRecentMealToPlate = (pastMeal: Meal) => {
    const pastDishes = pastMeal.dishes && pastMeal.dishes.length > 0
      ? pastMeal.dishes
      : [
          {
            id: `dish-${Date.now()}`,
            title: (pastMeal.description || 'SAVED MEAL').toUpperCase(),
            portionMultiplier: pastMeal.portionMultiplier || 1.0,
            ingredients: pastMeal.ingredients && pastMeal.ingredients.length > 0
              ? pastMeal.ingredients
              : convertTagsToIngredientItems(
                  pastMeal.ingredients_tags || pastMeal.description || 'Meal'
                ),
          },
        ];

    setDishes((prev) => {
      // If current plate only has 1 empty dish, replace it
      if (prev.length === 1 && prev[0].ingredients.length === 0) {
        return pastDishes;
      }
      return [...prev, ...pastDishes];
    });
    setActiveDishIndex(dishes.length);
    setActiveTab(null);
  };

  const handleAddSavedRecipeToPlate = (
    recipe: SavedRecipe,
    multiplier?: number,
    recipeServings?: number,
    portionsEaten?: number
  ) => {
    const serv = recipeServings || recipe.recipeServings || (recipe.portionSize && recipe.portionSize > 1 ? recipe.portionSize : 1);
    const eaten = portionsEaten !== undefined ? portionsEaten : (recipe.portionsEaten || (multiplier !== undefined ? multiplier * serv : 1));
    const effectiveMultiplier = multiplier !== undefined ? multiplier : Math.max(0.01, Math.round((eaten / serv) * 1000) / 1000);

    const newDish: PlateDish = {
      id: `dish-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      title: recipe.title.toUpperCase(),
      portionMultiplier: effectiveMultiplier,
      recipeServings: serv,
      portionsEaten: eaten,
      ingredients: recipe.ingredients.map((ing) => ({
        ...ing,
        id: `ing-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      })),
      notes: recipe.notes,
    };

    setDishes((prev) => {
      if (prev.length === 1 && prev[0].ingredients.length === 0) {
        return [newDish];
      }
      return [...prev, newDish];
    });
    setActiveDishIndex(dishes.length);
    setActiveTab(null);
  };

  const handleSaveCurrentPlateAsRecipe = () => {
    const targetIdx = ensureActiveDish();
    const currentDish = dishes[targetIdx];
    if (!currentDish || currentDish.ingredients.length === 0) {
      alert('Add ingredients to the dish before saving to library.');
      return;
    }

    const newRecipe: SavedRecipe = {
      id: `recipe-${Date.now()}`,
      title: currentDish.title || 'SAVED RECIPE',
      portionSize: currentDish.portionMultiplier || 1.0,
      category: mealCategory,
      createdAtIso: new Date().toISOString(),
      ingredients: currentDish.ingredients,
      notes,
    };

    localStore.saveRecipe(newRecipe);
    setSavedRecipes(localStore.getSavedRecipes());
    alert(`Saved "${newRecipe.title}" to Library.`);
  };

  // -------------------------------------------------------------
  // AI Plate & Describe Parsing
  // -------------------------------------------------------------
  const handleAiDescribeSubmit = async () => {
    const text = aiText.trim();
    if (!text) return;

    setIsAiLoading(true);
    setAiStatusMessage('Decomposing culinary ingredients & portions...');

    try {
      const result = await describeMealDirectOrProxy(text);
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Could not decompose meal description.');
      }

      const data = result.data;
      const dishTitle = (data.dish_name || text.slice(0, 30)).toUpperCase();
      const rawIngredients: any[] = Array.isArray(data.ingredients) ? data.ingredients : [];

      // Auto-register any novel ingredients discovered by AI into local & global database
      if (Array.isArray(data.discovered_foods) && data.discovered_foods.length > 0) {
        autoRegisterDiscoveredFoods(data.discovered_foods).catch(() => {});
      } else if (rawIngredients.length > 0) {
        autoRegisterDiscoveredFoods(rawIngredients).catch(() => {});
      }

      // Convert parsed ingredients using unified normalization engine
      const converted = normalizeParsedAiIngredients(rawIngredients);

      // Extract servings if specified in text (e.g. "Serves 4, ate 1 bowl") or use UI selector
      const extracted = extractRecipeServingsAndPortion(text);
      const effectiveServings = (extracted && extracted.recipeServings) ? extracted.recipeServings : (aiRecipeServings > 0 ? aiRecipeServings : 1);
      const effectivePortions = (extracted && extracted.portionsEaten) ? extracted.portionsEaten : (aiPortionsEaten > 0 ? aiPortionsEaten : 1);
      const effectiveMultiplier = Math.max(0.01, Math.round((effectivePortions / effectiveServings) * 1000) / 1000);

      const newDish: PlateDish = {
        id: `dish-${Date.now()}`,
        title: dishTitle,
        portionMultiplier: effectiveMultiplier,
        recipeServings: effectiveServings,
        portionsEaten: effectivePortions,
        ingredients: converted,
      };

      if (data.meal_category && MEAL_CATEGORIES.includes(data.meal_category)) {
        setMealCategory(data.meal_category as MealType);
      }

      setDishes((prev) => {
        if (prev.length === 1 && prev[0].ingredients.length === 0) {
          return [newDish];
        }
        return [...prev, newDish];
      });

      setAiText('');
      setAiRecipeServings(1);
      setAiPortionsEaten(1);
      setAiStatusMessage(null);
      setActiveTab(null);
    } catch (err: any) {
      console.warn('AI Describe error:', err);
      // Local fallback decomposition
      const extracted = extractRecipeServingsAndPortion(text);
      const effectiveServings = (extracted && extracted.recipeServings) ? extracted.recipeServings : (aiRecipeServings > 0 ? aiRecipeServings : 1);
      const effectivePortions = (extracted && extracted.portionsEaten) ? extracted.portionsEaten : (aiPortionsEaten > 0 ? aiPortionsEaten : 1);
      const effectiveMultiplier = Math.max(0.01, Math.round((effectivePortions / effectiveServings) * 1000) / 1000);

      const fallbackItems = convertTagsToIngredientItems(text);
      const fallbackDish: PlateDish = {
        id: `dish-${Date.now()}`,
        title: text.slice(0, 30).toUpperCase(),
        portionMultiplier: effectiveMultiplier,
        recipeServings: effectiveServings,
        portionsEaten: effectivePortions,
        ingredients: fallbackItems,
      };
      setDishes((prev) => (prev.length === 1 && prev[0].ingredients.length === 0 ? [fallbackDish] : [...prev, fallbackDish]));
      setAiText('');
      setAiRecipeServings(1);
      setAiPortionsEaten(1);
      setAiStatusMessage(null);
      setActiveTab(null);
    } finally {
      setIsAiLoading(false);
    }
  };

  const handlePlatePhotoUpload = async (file: File) => {
    try {
      setIsAiLoading(true);
      setAiStatusMessage('Optimizing photo for Vision scan...');
      const compressed = await compressImage(file, 1024, 0.82);
      setPlatePhotoPreview(compressed);

      setAiStatusMessage('Recognizing foods & estimating portion weights...');
      const data = await analyzeImageDirectOrProxy(compressed, dishes[activeDishIndex]?.title || '');

      if (data.success) {
        // Auto-register any novel ingredients discovered by AI into local & global database
        if (Array.isArray(data.discovered_foods) && data.discovered_foods.length > 0) {
          autoRegisterDiscoveredFoods(data.discovered_foods).catch(() => {});
        } else if (Array.isArray(data.ingredients) && data.ingredients.length > 0) {
          autoRegisterDiscoveredFoods(data.ingredients).catch(() => {});
        } else if (Array.isArray(data.ingredients_tags) && data.ingredients_tags.length > 0) {
          autoRegisterDiscoveredFoods(data.ingredients_tags).catch(() => {});
        }

        const rawIngredients = (Array.isArray(data.ingredients) && data.ingredients.length > 0)
          ? data.ingredients
          : (Array.isArray(data.ingredients_tags) && data.ingredients_tags.length > 0)
          ? data.ingredients_tags
          : [];

        let items = normalizeParsedAiIngredients(rawIngredients);

        const rawSuggested =
          data.suggested_name ||
          data.dish_name;
        let resolvedTitle = rawSuggested;
        if (!resolvedTitle || /^(AI\s*SCANNED\s*PLATE|SCANNED\s*PLATE|PLATE|MEAL|AI\s*PLATE|ANALYZED\s*PHOTO\s*\/\s*RECIPE)$/i.test(resolvedTitle)) {
          if (items.length >= 2) {
            resolvedTitle = `${items[0].name} & ${items[1].name}`;
          } else if (items.length === 1) {
            resolvedTitle = `${items[0].name} Meal`;
          } else {
            resolvedTitle = 'Home-Cooked Meal';
          }
        }
        const dishTitle = resolvedTitle.toUpperCase();
        const effectiveServings = aiRecipeServings > 0 ? aiRecipeServings : 1;
        const effectivePortions = aiPortionsEaten > 0 ? aiPortionsEaten : 1;
        const effectiveMultiplier = Math.max(0.01, Math.round((effectivePortions / effectiveServings) * 1000) / 1000);

        const newDish: PlateDish = {
          id: `dish-${Date.now()}`,
          title: dishTitle,
          portionMultiplier: effectiveMultiplier,
          recipeServings: effectiveServings,
          portionsEaten: effectivePortions,
          ingredients: items,
          notes: data.notes,
        };

        if (data.suggested_category && MEAL_CATEGORIES.includes(data.suggested_category)) {
          setMealCategory(data.suggested_category as MealType);
        }

        setDishes((prev) => {
          if (prev.length === 1 && prev[0].ingredients.length === 0) {
            return [newDish];
          }
          return [...prev, newDish];
        });
        setAiRecipeServings(1);
        setAiPortionsEaten(1);
        setActiveTab(null);
        setAiStatusMessage(null);
      } else {
        setAiStatusMessage(data.error || 'Could not analyze photo. Please try typing ingredients or snapping again.');
        setTimeout(() => setAiStatusMessage(null), 4000);
      }
    } catch (err: any) {
      console.warn('Vision scan error:', err);
      setAiStatusMessage(err?.name === 'AbortError' ? 'Scan timed out. Please try again.' : 'Failed to analyze photo.');
      setTimeout(() => setAiStatusMessage(null), 4000);
    } finally {
      setIsAiLoading(false);
    }
  };

  const captureAiPhotoFromStream = async () => {
    try {
      const file = await aiCameraViewfinderRef.current?.captureFrame();
      if (file) {
        await handlePlatePhotoUpload(file);
      }
    } catch (err) {
      console.warn('Capture photo from stream failed:', err);
    }
  };

  // Voice speech-to-text recognition
  const toggleSpeechRecognition = () => {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Voice dictation is not supported in this browser.');
      return;
    }

    if (aiIsListening) {
      setAiIsListening(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setAiIsListening(true);
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setAiText((prev) => (prev ? `${prev} ${transcript}` : transcript));
        setAiIsListening(false);
      };

      recognition.onerror = () => {
        setAiIsListening(false);
      };

      recognition.onend = () => {
        setAiIsListening(false);
      };

      recognition.start();
    } catch (e) {
      setAiIsListening(false);
    }
  };

  const handleBarcodePhotoUpload = async (file: File) => {
    if (!file) return;
    setIsAiLoading(true);
    setAiStatusMessage('Scanning image for barcode / product label...');
    try {
      const detectedBarcode = await decodeBarcodeFromPhoto(file);
      if (detectedBarcode) {
        setIsAiLoading(false);
        setAiStatusMessage(null);
        await lookupBarcode(detectedBarcode);
        return;
      }

      // If no barcode found in image, seamlessly run OCR & Ingredient Recognition
      setAiStatusMessage('No barcode detected — recognizing product label with Vision AI...');
      await handlePlatePhotoUpload(file);
    } catch (err) {
      console.warn('Barcode photo upload error:', err);
      setAiStatusMessage('Could not analyze photo. Please try typing the food name.');
      setTimeout(() => setAiStatusMessage(null), 3000);
    } finally {
      setIsAiLoading(false);
    }
  };

  // -------------------------------------------------------------
  // Save Full Meal
  // -------------------------------------------------------------
  const handleSaveMeal = () => {
    const activeDishes = dishes.filter((d) => d.ingredients.length > 0);
    if (activeDishes.length === 0) {
      alert('Please add at least one food or ingredient to your plate.');
      return;
    }

    const primaryTitle = activeDishes[0]?.title || 'MEAL';
    const allIngredientsList = activeDishes.flatMap((d) => d.ingredients);
    const tagsString = allIngredientsList.map((i) => i.canonicalName || i.name).join(', ');
    const mealDescription = synthesizeMealDescription(activeDishes) || primaryTitle;

    // Determine final timestamp: preserve exact timestamp_iso if user did not change date/time
    let finalTimestampIso: string;
    if (initialMeal?.timestamp_iso) {
      const origDateStr = extractLocalDateFromIso(initialMeal.timestamp_iso) || initialMeal.date;
      const origTimeStr = extractLocalTimeFromIso(initialMeal.timestamp_iso);

      if (entryDate === origDateStr && entryTime === origTimeStr) {
        finalTimestampIso = initialMeal.timestamp_iso;
      } else {
        finalTimestampIso = createIsoFromLocalDateAndTime(entryDate, entryTime);
      }
    } else {
      finalTimestampIso = createIsoFromLocalDateAndTime(entryDate, entryTime);
    }

    const newMeal: Omit<Meal, 'id'> = {
      timestamp_iso: finalTimestampIso,
      date: entryDate,
      meal_type: mealCategory,
      description: mealDescription,
      ingredients_tags: tagsString,
      ingredients: allIngredientsList,
      dishes: activeDishes,
      nutrition: plateNutrition,
      fodmap_rating: stackingReport.overallPlateRating,
      stacking_report: stackingReport,
      anti_aggravates: confirmedFreeFrom.join(', '),
      notes: notes.trim() || undefined,
    };

    onSaveMeal(newMeal);
    onClose();
  };

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={initialMeal?.id ? 'Edit meal' : 'Log meal'}
      fullHeight
      preventDismiss={hasPlateItems && !initialMeal?.id}
      className="bg-slate-50 dark:bg-zinc-950 dark:border-zinc-800"
    >
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-t-[32px] text-slate-900 dark:text-zinc-100">
        {/* =========================================================
            1. TOP HEADER & CONTEXT BAR
            ========================================================= */}
        <header
          className="px-4 py-3 bg-white/95 dark:bg-zinc-950/95 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between gap-2 shrink-0 z-20"
        >
          {/* Left Action: Back to Plate (if inside tab) or Plate Icon/Label */}
          {activeTab !== null && hasPlateItems ? (
            <button
              onClick={() => setActiveTab(null)}
              aria-label="Back to Plate"
              className="h-11 px-3.5 flex items-center gap-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-900 dark:hover:bg-zinc-800 text-slate-700 hover:text-slate-900 dark:text-zinc-200 active:scale-95 transition-all shrink-0 font-semibold text-xs"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Plate</span>
            </button>
          ) : (
            <div className="flex items-center gap-2 px-1 text-slate-800 dark:text-zinc-200 font-bold text-sm tracking-wide shrink-0">
              <Utensils className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
              <span className="hidden xs:inline font-semibold">Plate</span>
            </div>
          )}

          {/* Center Context Pills */}
          <div className="flex items-center gap-2 overflow-x-auto py-1 scrollbar-none flex-1 justify-center max-w-xs">
            {/* Time Pill */}
            <button
              onClick={() => setShowTimePicker(true)}
              className="h-11 px-3.5 flex items-center gap-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 dark:bg-zinc-900 dark:hover:bg-zinc-800 dark:border-zinc-800 dark:text-zinc-200 text-xs font-medium text-slate-800 active:scale-95 transition-all shrink-0"
              title="Set Meal Time"
            >
              <Clock className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />
              <span>{entryTime}</span>
            </button>

            {/* Meal Settings & Category Pill */}
            <button
              onClick={() => {
                setShowMealSettings(true);
                setShowDeleteConfirm(false);
              }}
              className="h-11 px-3.5 flex items-center gap-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 dark:bg-zinc-900 dark:hover:bg-zinc-800 dark:border-zinc-800 dark:text-zinc-200 text-xs font-medium text-slate-800 active:scale-95 transition-all shrink-0"
              title="Meal Category Settings"
            >
              <span>{mealCategory}</span>
              <Settings className="w-3 h-3 text-slate-400 ml-0.5" />
            </button>
          </div>

          {/* Right Action: Desktop Log Foods, Direct Delete (if editing), & Modal Close Button */}
          <div className="flex items-center gap-1.5 shrink-0">
            {initialMeal?.id && onDeleteMeal && (
              <button
                type="button"
                onClick={() => {
                  setShowMealSettings(true);
                  setShowDeleteConfirm(true);
                }}
                aria-label="Delete Meal"
                title="Delete Meal"
                className="w-11 h-11 flex items-center justify-center rounded-xl bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-950/70 text-rose-600 dark:text-rose-400 active:scale-95 transition-all shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            {hasPlateItems && (
              <button
                onClick={handleSaveMeal}
                className="hidden sm:flex h-11 px-4 items-center gap-2 rounded-xl bg-slate-900 hover:bg-slate-800 dark:bg-zinc-100 dark:hover:bg-zinc-200 text-white dark:text-zinc-950 font-semibold text-xs tracking-wide active:scale-95 transition-all shadow-sm shrink-0"
              >
                <Check className="w-4 h-4" />
                <span>Log Foods</span>
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              className="w-11 h-11 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-900 dark:hover:bg-zinc-800 text-slate-600 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-zinc-200 active:scale-95 transition-all shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* =========================================================
            2. THE 4 NAVIGATION TABS (Search, Scan, AI, Library)
            ========================================================= */}
        <nav className="px-4 py-2.5 bg-white dark:bg-zinc-950 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between gap-1.5 shrink-0">
          <button
            onClick={() => handleTabClick('search')}
            className={`flex-1 h-11 flex items-center justify-center gap-2 rounded-xl text-xs font-medium transition-all ${
              activeTab === 'search'
                ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 font-semibold shadow-sm'
                : 'bg-slate-50 border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:bg-zinc-900 dark:border-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-zinc-800'
            }`}
          >
            <Search className="w-4 h-4" />
            <span>Search</span>
          </button>

          <button
            onClick={() => handleTabClick('scan')}
            className={`flex-1 h-11 flex items-center justify-center gap-2 rounded-xl text-xs font-medium transition-all ${
              activeTab === 'scan'
                ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 font-semibold shadow-sm'
                : 'bg-slate-50 border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:bg-zinc-900 dark:border-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-zinc-800'
            }`}
          >
            <QrCode className="w-4 h-4" />
            <span>Scan</span>
          </button>

          <button
            onClick={() => handleTabClick('ai')}
            className={`flex-1 h-11 flex items-center justify-center gap-2 rounded-xl text-xs font-medium transition-all ${
              activeTab === 'ai'
                ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 font-semibold shadow-sm'
                : 'bg-slate-50 border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:bg-zinc-900 dark:border-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-zinc-800'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            <span>AI</span>
          </button>

          <button
            onClick={() => handleTabClick('library')}
            className={`flex-1 h-11 flex items-center justify-center gap-2 rounded-xl text-xs font-medium transition-all ${
              activeTab === 'library'
                ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 font-semibold shadow-sm'
                : 'bg-slate-50 border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:bg-zinc-900 dark:border-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-zinc-800'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            <span>Library</span>
          </button>
        </nav>

        {/* =========================================================
            SPEED-DEPENDENT COLLAPSIBLE IMAGE BANNER
            Borderless, no rounded corners, no weird bottom overlay.
            Collapses on scroll down; expands & reappears on scroll up.
            ========================================================= */}
        {platePhotoPreview && activeTab === null && (
          <div
            style={{
              height: `${Math.max(0, 220 - workspaceScrollY)}px`,
              opacity: Math.max(0, 1 - workspaceScrollY / 180),
              transition: 'height 0.05s ease-out, opacity 0.05s ease-out',
            }}
            className="w-full overflow-hidden relative shrink-0 bg-slate-100 dark:bg-zinc-900"
          >
            <img
              src={platePhotoPreview}
              alt="Meal Photo"
              className="w-full h-full object-cover rounded-none"
            />
            <button
              type="button"
              onClick={() => {
                setActiveTab('ai');
                setAiSubTab('snap');
                setPlatePhotoPreview(null);
              }}
              className="absolute top-3 right-3 px-3 py-1.5 rounded-xl bg-white/95 hover:bg-white dark:bg-zinc-900/90 dark:text-zinc-100 dark:border-zinc-800 dark:hover:bg-zinc-800 text-slate-900 text-[11px] font-bold shadow-md active:scale-95 transition-all border border-slate-200"
            >
              Retake Photo
            </button>
          </div>
        )}

        {/* =========================================================
            3. SCROLLABLE WORKSPACE
            ========================================================= */}
        <div
          onScroll={(e) => setWorkspaceScrollY(e.currentTarget.scrollTop)}
          className={`flex-1 overflow-y-auto p-4 space-y-6 ${hasPlateItems ? 'pb-28 sm:pb-32' : ''}`}
        >
          {/* Active Tab Panel (Search, Scan, AI, Library) */}
          {activeTab !== null && (
            <div className="p-4 rounded-2xl bg-white dark:bg-zinc-900/60 border border-slate-200 dark:border-zinc-800 shadow-sm space-y-4">
              {hasPlateItems ? (
                <div className="flex items-center justify-between pb-2 border-b border-slate-200 dark:border-zinc-800">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400">
                    Add to Plate ({activeTab.toUpperCase()})
                  </span>
                  <button
                    onClick={() => setActiveTab(null)}
                    className="h-8 px-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-xs font-medium text-slate-700 dark:text-zinc-300 flex items-center gap-1 transition-colors"
                  >
                    <ChevronUp className="w-3.5 h-3.5" />
                    <span>Hide</span>
                  </button>
                </div>
              ) : (
                <div className="text-center py-1 pb-2 border-b border-slate-100 dark:border-zinc-800/60">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-zinc-100">
                    Add Food to Plate
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">
                    Search ingredients, scan a barcode, or snap a plate to begin
                  </p>
                </div>
              )}

              {/* ----------------- TAB: SEARCH ----------------- */}
              {activeTab === 'search' && (
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-zinc-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search past meals, foods, or type ingredient..."
                      className="w-full h-12 pl-11 pr-4 rounded-xl bg-slate-100 dark:bg-zinc-900 border border-slate-300 dark:border-zinc-700 text-sm text-slate-900 dark:text-zinc-100 placeholder-slate-400 dark:placeholder-zinc-500 focus:outline-none focus:border-slate-500 transition-colors"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-white p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Search Results: Local FODMAP Database */}
                  {searchResults.length > 0 && (
                    <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                      <div className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 dark:text-zinc-400 px-1">
                        Open FODMAP Database Results
                      </div>
                      {searchResults.map((food) => {
                        const ratingColor =
                          food.rating === 'low'
                            ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
                            : food.rating === 'moderate'
                            ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30'
                            : 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30';

                        return (
                          <button
                            key={food.id}
                            onClick={() => handleSelectSearchResult(food)}
                            className="w-full min-h-[44px] px-3.5 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 dark:bg-zinc-900 dark:hover:bg-zinc-800 border border-slate-200 dark:border-zinc-800/80 flex items-center justify-between text-left transition-colors"
                          >
                            <div className="flex items-center gap-2.5 truncate pr-2">
                              <span className="text-sm font-medium text-slate-900 dark:text-zinc-200 truncate">
                                {food.name}
                              </span>
                              <span className="text-[10px] text-slate-400 dark:text-zinc-500 truncate hidden sm:inline">
                                {food.category}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span
                                className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${ratingColor}`}
                              >
                                {food.rating}
                              </span>
                              <span className="text-xs font-semibold text-slate-600 dark:text-zinc-300">
                                + Add
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Branded Products: Open Food Facts Database */}
                  {searchQuery.trim().length >= 2 && (
                    <div className="space-y-2 pt-2 border-t border-slate-200/80 dark:border-zinc-800">
                      <div className="flex items-center justify-between px-1">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-slate-600 dark:text-zinc-400">
                          <Package className="w-3.5 h-3.5 text-indigo-500" />
                          <span>Branded Products (Open Food Facts)</span>
                        </div>
                        {isSearchingOff && (
                          <div className="flex items-center gap-1 text-[10px] text-slate-400 dark:text-zinc-500">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            <span>Searching...</span>
                          </div>
                        )}
                      </div>

                      {offResults.length > 0 ? (
                        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                          {offResults.map((product) => (
                            <div
                              key={product.code || product.name}
                              className="p-3 rounded-xl bg-slate-50 dark:bg-zinc-900/90 border border-slate-200 dark:border-zinc-800 flex items-center justify-between gap-3 hover:border-slate-300 dark:hover:border-zinc-700 transition-colors"
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                {product.imageUrl ? (
                                  <img
                                    src={product.imageUrl}
                                    alt={product.name}
                                    className="w-10 h-10 rounded-lg object-cover bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 shrink-0"
                                    onError={(e) => {
                                      (e.currentTarget as HTMLElement).style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-lg bg-slate-200 dark:bg-zinc-800 flex items-center justify-center text-slate-500 shrink-0">
                                    <Package className="w-5 h-5 opacity-60" />
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-xs font-bold text-slate-900 dark:text-zinc-100 truncate max-w-[200px]">
                                      {product.name}
                                    </span>
                                    {product.brand && (
                                      <span className="px-1.5 py-0.2 rounded text-[9px] font-semibold bg-indigo-50 dark:bg-indigo-950/70 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/60">
                                        {product.brand}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[10px] text-slate-500 dark:text-zinc-400 truncate mt-0.5">
                                    {product.ingredients.length > 0
                                      ? `${product.ingredients.length} ingredients: ${product.ingredients.slice(0, 4).join(', ')}${product.ingredients.length > 4 ? '...' : ''}`
                                      : 'Commercial food product'}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-1.5 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => handleAddOffProductToPlate(product, false)}
                                  className="min-h-[36px] px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 text-xs font-semibold flex items-center gap-1 transition-colors shadow-xs"
                                  title="Add ingredients to active dish"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  <span>Add</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleAddOffProductToPlate(product, true)}
                                  className="min-h-[36px] px-2.5 py-1.5 rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-200 text-xs font-semibold flex items-center gap-1 transition-colors"
                                  title="Add as distinct new dish"
                                >
                                  <Utensils className="w-3.5 h-3.5" />
                                  <span>Dish</span>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        !isSearchingOff && (
                          <div className="p-3 rounded-xl bg-slate-50/70 dark:bg-zinc-900/40 border border-slate-200/50 dark:border-zinc-800/60 text-center text-xs text-slate-400 dark:text-zinc-500">
                            No matching branded items found on Open Food Facts for "{searchQuery.trim()}".
                          </div>
                        )
                      )}
                    </div>
                  )}

                  {/* Quick Add Fallback Options whenever user has typed a query */}
                  {searchQuery.trim().length > 0 && (
                    <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/80 border border-slate-200 dark:border-zinc-800 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 dark:text-zinc-400">
                          {searchResults.length === 0 && offResults.length === 0 ? 'Not found in databases' : 'Direct Plate Actions'}
                        </span>
                        <span className="text-xs text-slate-700 dark:text-zinc-300 font-bold truncate max-w-[180px]">
                          "{searchQuery.trim()}"
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const targetIdx = ensureActiveDish();
                            handleAddIngredientToDish(targetIdx, searchQuery.trim());
                            setSearchQuery('');
                            setOffResults([]);
                            setActiveTab(null);
                          }}
                          className="min-h-[44px] px-3.5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors shadow-xs"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add to Current Dish</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            handleAddCustomMealAsNewDish(searchQuery.trim());
                          }}
                          className="min-h-[44px] px-3.5 py-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-200 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                        >
                          <Utensils className="w-4 h-4" />
                          <span>Add as New Dish</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Recents List for 1-Tap Re-Log */}
                  {!searchQuery && recentMeals.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 dark:text-zinc-400 px-1">
                        Recent Meals (1-Tap Re-Log)
                      </div>
                      <div className="space-y-1.5 max-h-56 overflow-y-auto">
                        {recentMeals.slice(0, 5).map((m) => (
                          <div
                            key={m.id}
                            className="w-full p-3 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800/80 flex items-center justify-between gap-3"
                          >
                            <div className="truncate">
                              <div className="text-xs font-semibold uppercase text-slate-900 dark:text-zinc-200 truncate">
                                {m.description || 'Meal'}
                              </div>
                              <div className="text-[11px] text-slate-500 dark:text-zinc-400 truncate mt-0.5">
                                {m.ingredients_tags || 'Ingredients'}
                              </div>
                            </div>
                            <button
                              onClick={() => handleAddRecentMealToPlate(m)}
                              className="min-h-[40px] px-3.5 py-2 rounded-xl bg-slate-200 hover:bg-slate-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-xs font-medium text-slate-800 dark:text-zinc-200 shrink-0 transition-colors"
                            >
                              + Add to Plate
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ----------------- TAB: SCAN ----------------- */}
              {activeTab === 'scan' && (
                <div className="space-y-3.5">
                  {/* Sub-tabs: Barcode vs Label */}
                  <div className="flex rounded-xl bg-slate-100 dark:bg-zinc-900 p-1 border border-slate-200 dark:border-zinc-800">
                    <button
                      onClick={() => {
                        setScanSubTab('barcode');
                      }}
                      className={`flex-1 py-2.5 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                        scanSubTab === 'barcode'
                          ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 font-semibold shadow'
                          : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                      }`}
                    >
                      <QrCode className="w-3.5 h-3.5" />
                      <span>Barcode Scanner</span>
                    </button>
                    <button
                      onClick={() => {
                        setScanSubTab('label');
                        stopAllCameras();
                      }}
                      className={`flex-1 py-2.5 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                        scanSubTab === 'label'
                          ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 font-semibold shadow'
                          : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                      }`}
                    >
                      <FileText className="w-3.5 h-3.5" />
                      <span>Nutrition Label</span>
                    </button>
                  </div>

                  {scanSubTab === 'barcode' && (
                    <div className="space-y-3">
                      {/* Hidden input for barcode photo upload */}
                      <input
                        ref={barcodePhotoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onClick={(e) => {
                          (e.target as HTMLInputElement).value = '';
                        }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleBarcodePhotoUpload(file);
                        }}
                      />

                      {/* Live Barcode Viewfinder Frame (Expanded immersive height) */}
                      <CameraViewfinder
                        ref={scannerViewfinderRef}
                        aspectRatioClass="aspect-[4/3] sm:aspect-video"
                        minHeightClass="min-h-[340px] sm:min-h-[400px]"
                        onReady={({ video }) => {
                          startScanningVideo(video);
                        }}
                      >
                        {/* Scanning Guide Box */}
                        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                          <div className="w-64 h-36 sm:w-80 sm:h-44 border-2 border-dashed border-emerald-400/90 rounded-2xl relative shadow-2xl">
                            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-rose-500/80 animate-pulse shadow-sm" />
                          </div>
                        </div>

                        {/* Viewfinder Controls Overlay */}
                        <div className="absolute top-3 inset-x-3 flex items-center justify-between pointer-events-auto">
                          <button
                            type="button"
                            onClick={() => setScanSubTab('label')}
                            className="h-9 px-3 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md text-white text-xs font-semibold flex items-center gap-1.5 transition-colors border border-white/20"
                          >
                            <X className="w-3.5 h-3.5" />
                            <span>Close</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => scannerViewfinderRef.current?.toggleTorch()}
                            className="h-9 px-3 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md text-white text-xs font-semibold flex items-center gap-1.5 transition-all border border-white/20"
                          >
                            <Zap className="w-3.5 h-3.5" />
                            <span>Torch</span>
                          </button>
                        </div>

                        <div className="absolute bottom-3 inset-x-3 text-center">
                          <span className="px-4 py-1.5 rounded-full bg-black/75 backdrop-blur-md text-xs text-white font-medium shadow-md">
                            🔄 360° Omnidirectional Scan • Point at barcode
                          </span>
                        </div>
                      </CameraViewfinder>

                      {scannerStatus && (
                        <p className="text-xs text-slate-500 dark:text-zinc-400 text-center font-medium">{scannerStatus}</p>
                      )}

                      {/* Manual Barcode Entry + Upload Row */}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={barcodeInput}
                          onChange={(e) => setBarcodeInput(e.target.value)}
                          placeholder="Or enter barcode digits..."
                          className="flex-1 h-11 px-3.5 rounded-xl bg-slate-100 dark:bg-zinc-900 border border-slate-300 dark:border-zinc-700 text-xs text-slate-900 dark:text-zinc-200 placeholder-slate-400 dark:placeholder-zinc-500 focus:outline-none"
                        />
                        <button
                          onClick={() => lookupBarcode(barcodeInput)}
                          className="h-11 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 text-xs font-semibold transition-colors"
                        >
                          Lookup
                        </button>
                        <button
                          type="button"
                          onClick={() => barcodePhotoInputRef.current?.click()}
                          title="Upload barcode image"
                          className="h-11 w-11 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-900 dark:hover:bg-zinc-800 border border-slate-300 dark:border-zinc-700 text-slate-700 dark:text-zinc-300 transition-colors"
                        >
                          <Upload className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {scanSubTab === 'label' && (
                    <div className="space-y-4 text-center py-4">
                      <p className="text-xs text-slate-500 dark:text-zinc-400 leading-relaxed max-w-sm mx-auto">
                        Photograph or upload the ingredient list on food packaging to automatically extract all
                        ingredients and detect hidden FODMAP culprits.
                      </p>
                      <input
                        ref={labelFileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onClick={(e) => {
                          (e.target as HTMLInputElement).value = '';
                        }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handlePlatePhotoUpload(file);
                        }}
                      />
                      <input
                        ref={labelGalleryInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onClick={(e) => {
                          (e.target as HTMLInputElement).value = '';
                        }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handlePlatePhotoUpload(file);
                        }}
                      />
                      <div className="grid grid-cols-2 gap-2.5 max-w-sm mx-auto">
                        <button
                          type="button"
                          onClick={() => labelFileInputRef.current?.click()}
                          className="min-h-[48px] rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 font-semibold text-xs flex items-center justify-center gap-2 shadow transition-all active:scale-95"
                        >
                          <Camera className="w-4 h-4" />
                          <span>Take Photo</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => labelGalleryInputRef.current?.click()}
                          className="min-h-[48px] rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-900 dark:hover:bg-zinc-800 border border-slate-300 dark:border-zinc-700 text-slate-800 dark:text-zinc-200 font-semibold text-xs flex items-center justify-center gap-2 transition-all active:scale-95"
                        >
                          <Upload className="w-4 h-4" />
                          <span>Upload Photo</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ----------------- TAB: AI ----------------- */}
              {activeTab === 'ai' && (
                <div className="space-y-3.5">
                  {/* Hidden File Pickers */}
                  <input
                    ref={cameraFileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onClick={(e) => {
                      (e.target as HTMLInputElement).value = '';
                    }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handlePlatePhotoUpload(file);
                    }}
                  />
                  <input
                    ref={galleryFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onClick={(e) => {
                      (e.target as HTMLInputElement).value = '';
                    }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handlePlatePhotoUpload(file);
                    }}
                  />

                  {aiSubTab === 'snap' && (
                    <div className="space-y-3">
                      {platePhotoPreview ? (
                        <div className="relative w-full aspect-[3/4] sm:aspect-[4/3] min-h-[420px] sm:min-h-[480px] max-h-[70vh] rounded-3xl overflow-hidden border border-slate-200 dark:border-zinc-800 shadow-2xl">
                          <img
                            src={platePhotoPreview}
                            alt="Plate"
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => {
                              setPlatePhotoPreview(null);
                            }}
                            className="absolute top-3 right-3 p-2.5 rounded-2xl bg-black/70 text-white hover:bg-black/90 shadow-lg"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <CameraViewfinder
                          ref={aiCameraViewfinderRef}
                          aspectRatioClass="aspect-[3/4] sm:aspect-[4/3]"
                          minHeightClass="min-h-[440px] sm:min-h-[520px] max-h-[70vh]"
                        >
                          {/* Large Framing Overlay Guides */}
                          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                            <div className="w-64 h-64 sm:w-80 sm:h-80 border-2 border-white/40 rounded-3xl relative shadow-2xl">
                              <div className="absolute -top-1.5 -left-1.5 w-6 h-6 border-t-4 border-l-4 border-emerald-400 rounded-tl-xl" />
                              <div className="absolute -top-1.5 -right-1.5 w-6 h-6 border-t-4 border-r-4 border-emerald-400 rounded-tr-xl" />
                              <div className="absolute -bottom-1.5 -left-1.5 w-6 h-6 border-b-4 border-l-4 border-emerald-400 rounded-bl-xl" />
                              <div className="absolute -bottom-1.5 -right-1.5 w-6 h-6 border-b-4 border-r-4 border-emerald-400 rounded-br-xl" />
                            </div>
                          </div>

                          {/* Top Camera Overlay: Live Tag + OVERLAY BUTTON TO MOVE OVER TO DESCRIBE */}
                          <div className="absolute top-3.5 inset-x-3.5 flex items-center justify-between z-10 pointer-events-auto">
                            <div className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-black/60 backdrop-blur-md text-white text-xs font-semibold border border-white/15 shadow-md">
                              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                              <span>AI Vision</span>
                            </div>

                            {/* OVERLAY SWITCH TO DESCRIBE */}
                            <button
                              type="button"
                              onClick={() => handleSwitchAiSubTab('describe')}
                              className="px-4 py-2 rounded-full bg-white/95 hover:bg-white text-slate-900 font-bold text-xs flex items-center gap-2 shadow-xl active:scale-95 transition-all"
                            >
                              <FileText className="w-4 h-4 text-slate-700" />
                              <span>Describe Mode</span>
                              <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                            </button>
                          </div>

                          {/* Bottom Camera Controls Overlay (Shutter + Upload + Native Camera) */}
                          <div className="absolute bottom-4 inset-x-4 flex items-center justify-between z-10 px-2 pointer-events-auto">
                            {/* Upload from Gallery Button */}
                            <button
                              type="button"
                              onClick={() => galleryFileInputRef.current?.click()}
                              className="w-13 h-13 p-3 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/25 text-white flex items-center justify-center active:scale-95 transition-all shadow-lg"
                              title="Upload Photo from Gallery"
                            >
                              <Upload className="w-5 h-5" />
                            </button>

                            {/* Large Tactile Camera Shutter Button */}
                            <button
                              type="button"
                              onClick={captureAiPhotoFromStream}
                              className="w-20 h-20 rounded-full border-4 border-white bg-white/20 active:bg-white/40 flex items-center justify-center transition-all active:scale-90 shadow-2xl"
                              title="Capture Photo"
                            >
                              <div className="w-15 h-15 rounded-full bg-white shadow-xl" />
                            </button>

                            {/* Native Camera Trigger */}
                            <button
                              type="button"
                              onClick={() => cameraFileInputRef.current?.click()}
                              className="w-13 h-13 p-3 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/25 text-white flex items-center justify-center active:scale-95 transition-all shadow-lg"
                              title="Open Native Camera App"
                            >
                              <Camera className="w-5 h-5" />
                            </button>
                          </div>
                        </CameraViewfinder>
                      )}

                      {isAiLoading && (
                        <div className="flex items-center justify-center gap-2.5 py-3 text-xs text-slate-700 dark:text-zinc-300 font-medium">
                          <Loader2 className="w-4 h-4 animate-spin text-slate-500 dark:text-zinc-400" />
                          <span>{aiStatusMessage || 'Processing meal...'}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {aiSubTab === 'describe' && (
                    <div className="space-y-3">
                      {/* Top Header with Switch to Camera Button */}
                      <div className="flex items-center justify-between pb-1">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400">
                          Describe or Dictate Meal
                        </span>
                        {/* OVERLAY SWITCH BACK TO CAMERA */}
                        <button
                          type="button"
                          onClick={() => handleSwitchAiSubTab('snap')}
                          className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-200 font-semibold text-xs flex items-center gap-1.5 transition-all active:scale-95"
                        >
                          <Camera className="w-3.5 h-3.5" />
                          <span>Switch to Camera</span>
                        </button>
                      </div>

                      <div className="relative">
                        <textarea
                          value={aiText}
                          onChange={(e) => setAiText(e.target.value)}
                          placeholder="e.g. Chicken and mushroom risotto made with arborio rice, oyster mushrooms, chicken stock, garlic-infused olive oil, butter, and grated parmesan cheese..."
                          rows={3}
                          className="w-full p-3.5 pr-12 rounded-xl bg-slate-100 dark:bg-zinc-900 border border-slate-300 dark:border-zinc-700 text-xs text-slate-900 dark:text-zinc-100 placeholder-slate-400 dark:placeholder-zinc-500 focus:outline-none focus:border-slate-500 leading-relaxed"
                        />
                        <button
                          onClick={toggleSpeechRecognition}
                          aria-label="Voice Dictation"
                          className={`absolute right-3 bottom-3 p-2 rounded-xl transition-colors ${
                            aiIsListening
                              ? 'bg-rose-600 text-white animate-pulse'
                              : 'bg-slate-200 hover:bg-slate-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-700 dark:text-zinc-300'
                          }`}
                        >
                          {aiIsListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                        </button>
                      </div>

                      {/* Recipe Yield & Portion Selector for Describe */}
                      <div className="p-3 bg-slate-100 dark:bg-zinc-900/80 rounded-xl border border-slate-200 dark:border-zinc-800 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-semibold text-slate-700 dark:text-zinc-300 uppercase tracking-wider">
                            Recipe Yield & Portion Eaten
                          </span>
                          <span className="text-[11px] font-medium text-slate-500 dark:text-zinc-400">
                            {aiPortionsEaten} of {aiRecipeServings} {aiRecipeServings === 1 ? 'serving' : 'servings'} ({Math.round((aiPortionsEaten / aiRecipeServings) * 1000) / 10}%)
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <label className="block text-[10px] uppercase font-semibold text-slate-500 dark:text-zinc-400 mb-1">
                              Recipe Serves
                            </label>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => setAiRecipeServings(Math.max(1, aiRecipeServings - 1))}
                                className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-zinc-800 text-slate-800 dark:text-zinc-200 font-bold flex items-center justify-center hover:bg-slate-300 dark:hover:bg-zinc-700"
                              >
                                -
                              </button>
                              <span className="flex-1 text-center font-bold text-slate-900 dark:text-zinc-100 text-xs">
                                {aiRecipeServings} {aiRecipeServings === 1 ? 'serv' : 'servs'}
                              </span>
                              <button
                                type="button"
                                onClick={() => setAiRecipeServings(aiRecipeServings + 1)}
                                className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-zinc-800 text-slate-800 dark:text-zinc-200 font-bold flex items-center justify-center hover:bg-slate-300 dark:hover:bg-zinc-700"
                              >
                                +
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {[1, 2, 4, 6, 8].map((s) => (
                                <button
                                  key={s}
                                  type="button"
                                  onClick={() => setAiRecipeServings(s)}
                                  className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                                    aiRecipeServings === s
                                      ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950'
                                      : 'bg-slate-200/70 hover:bg-slate-300 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300'
                                  }`}
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase font-semibold text-slate-500 dark:text-zinc-400 mb-1">
                              Portion You Ate
                            </label>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => setAiPortionsEaten(Math.max(0.25, Math.round((aiPortionsEaten - 0.5) * 100) / 100))}
                                className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-zinc-800 text-slate-800 dark:text-zinc-200 font-bold flex items-center justify-center hover:bg-slate-300 dark:hover:bg-zinc-700"
                              >
                                -
                              </button>
                              <span className="flex-1 text-center font-bold text-slate-900 dark:text-zinc-100 text-xs">
                                {aiPortionsEaten} {aiPortionsEaten === 1 ? 'portion' : 'portions'}
                              </span>
                              <button
                                type="button"
                                onClick={() => setAiPortionsEaten(Math.round((aiPortionsEaten + 0.5) * 100) / 100)}
                                className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-zinc-800 text-slate-800 dark:text-zinc-200 font-bold flex items-center justify-center hover:bg-slate-300 dark:hover:bg-zinc-700"
                              >
                                +
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {[0.5, 1, 1.5, 2, aiRecipeServings].filter((v, idx, arr) => arr.indexOf(v) === idx).map((p) => (
                                <button
                                  key={p}
                                  type="button"
                                  onClick={() => setAiPortionsEaten(p)}
                                  className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                                    aiPortionsEaten === p
                                      ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950'
                                      : 'bg-slate-200/70 hover:bg-slate-300 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300'
                                  }`}
                                >
                                  {p === aiRecipeServings && p > 1 ? 'All' : `${p}`}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={handleAiDescribeSubmit}
                        disabled={isAiLoading || !aiText.trim()}
                        className="w-full min-h-[48px] rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 font-semibold text-xs tracking-wide flex items-center justify-center gap-2 shadow"
                      >
                        {isAiLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                        <span>Decompose Meal with AI</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ----------------- TAB: LIBRARY ----------------- */}
              {activeTab === 'library' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 dark:text-zinc-400">
                      Saved Recipes & Staples
                    </div>
                    <button
                      onClick={handleSaveCurrentPlateAsRecipe}
                      className="min-h-[38px] px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-xs font-medium text-slate-800 dark:text-zinc-200 transition-colors"
                    >
                      + Save Current Dish to Library
                    </button>
                  </div>

                  <div className="space-y-2.5 max-h-60 overflow-y-auto">
                    {savedRecipes.map((recipe) => (
                      <div
                        key={recipe.id}
                        className="p-3.5 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800/80 space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-xs tracking-wider uppercase text-slate-900 dark:text-zinc-100">
                            {recipe.title}
                          </div>
                          <span className="text-[10px] px-2 py-0.5 rounded bg-slate-200 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 uppercase font-mono">
                            {recipe.category || 'Recipe'}
                          </span>
                        </div>

                        <div className="text-[11px] text-slate-500 dark:text-zinc-400 truncate">
                          {recipe.ingredients.map((i) => i.name).join(', ')}
                        </div>

                        <div className="flex items-center justify-between pt-1">
                          <span className="text-xs text-slate-500 dark:text-zinc-400 font-mono">
                            {recipe.ingredients.length} items • {recipe.portionSize || 1} serving
                          </span>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleAddSavedRecipeToPlate(recipe, 0.5)}
                              className="h-8 px-2.5 rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-[11px] text-slate-700 dark:text-zinc-300 font-medium"
                            >
                              0.5x
                            </button>
                            <button
                              onClick={() => handleAddSavedRecipeToPlate(recipe, 1.0)}
                              className="h-8 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 text-[11px] font-semibold"
                            >
                              1.0x Add
                            </button>
                            <button
                              onClick={() => handleAddSavedRecipeToPlate(recipe, 1.5)}
                              className="h-8 px-2.5 rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-[11px] text-slate-700 dark:text-zinc-300 font-medium"
                            >
                              1.5x
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* =========================================================
              4. MULTI-DISH PLATE CONTAINER (MacroFactor-style Stack)
              ========================================================= */}
          {hasPlateItems ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400">
                  Plate Foods ({dishes.length} {dishes.length === 1 ? 'Dish' : 'Dishes'})
                </h3>
              </div>

              {/* Dish Cards Stack */}
              <div className="space-y-3">
                {dishes.map((dish, dishIdx) => {
                  const isCollapsed = !!collapsedDishes[dish.id];
                  const multiplier = dish.portionMultiplier || 1.0;

                  // Dish-specific total grams
                  let dishGrams = 0;
                  for (const ing of dish.ingredients) {
                    dishGrams += (ing.gramsEstimate || 0) * multiplier;
                  }

                  return (
                    <div
                      key={dish.id}
                      className="rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 overflow-hidden shadow-sm transition-all"
                    >
                      {/* Dish Header */}
                      <div className="p-4 border-b border-slate-100 dark:border-zinc-800/80 bg-slate-50/70 dark:bg-zinc-900/90 flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 space-y-1 pr-1">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setEditingDishIndex(dishIdx);
                                setEditDishName(dish.title);
                              }}
                              className="text-sm font-bold tracking-wider uppercase text-slate-900 dark:text-zinc-100 hover:text-slate-700 dark:hover:text-white flex items-center gap-1.5 min-w-0 text-left group"
                            >
                              <span className="line-clamp-2 break-words leading-snug">{dish.title || 'DISH'}</span>
                              <Edit3 className="w-3.5 h-3.5 text-slate-400 dark:text-zinc-500 shrink-0 group-hover:text-slate-600 dark:group-hover:text-zinc-300 transition-colors" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap pt-0.5">
                            <span className="text-xs font-mono text-slate-500 dark:text-zinc-400">
                              {dish.ingredients.length} {dish.ingredients.length === 1 ? 'ingredient' : 'ingredients'}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setEditingDishWeight({
                                  dishIndex: dishIdx,
                                  currentGrams: Math.round(dishGrams),
                                  dishTitle: dish.title || 'Dish',
                                })
                              }
                              className="px-2 py-0.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/60 dark:hover:bg-emerald-900/80 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 text-[11px] font-bold flex items-center gap-1 transition-colors shadow-2xs"
                              title="Click to scale total pack/dish weight"
                            >
                              <span>📦 {Math.round(dishGrams)}g</span>
                              <span className="text-[9px] font-normal text-emerald-600 dark:text-emerald-400">• Scale</span>
                            </button>
                          </div>
                        </div>

                        {/* Right Portion Multiplier Button */}
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() =>
                              setEditingPortionDish({
                                dishIndex: dishIdx,
                                currentMultiplier: multiplier,
                                recipeServings: dish.recipeServings,
                                portionsEaten: dish.portionsEaten,
                              })
                            }
                            className="min-h-[44px] px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 border border-slate-200 dark:border-zinc-700/60 text-xs font-semibold text-slate-800 dark:text-zinc-100 flex flex-col items-center justify-center leading-tight transition-colors shadow-sm"
                          >
                            <span className="text-[13px]">{multiplier}x</span>
                            <span className="text-[10px] text-slate-500 dark:text-zinc-400 uppercase font-normal">
                              {dish.recipeServings && dish.recipeServings > 1
                                ? `${dish.portionsEaten || (multiplier * dish.recipeServings)} of ${dish.recipeServings} serv`
                                : (multiplier === 1 ? '1 serving' : `${multiplier} servings`)}
                            </span>
                          </button>

                          {dishes.length > 1 && (
                            <button
                              onClick={() => handleRemoveDish(dishIdx)}
                              aria-label="Remove Dish"
                              className="w-11 h-11 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-rose-50 dark:bg-zinc-800/60 dark:hover:bg-rose-950/40 text-slate-400 hover:text-rose-600 dark:hover:text-rose-300 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Accordion Toggle Bar */}
                      <div className="px-4 py-2 bg-slate-100/60 dark:bg-zinc-950/60 border-b border-slate-100 dark:border-zinc-800/40 flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400">
                        <button
                          onClick={() => toggleDishCollapse(dish.id)}
                          className="flex items-center gap-1.5 hover:text-slate-900 dark:hover:text-zinc-200 transition-colors font-medium py-1"
                        >
                          {isCollapsed ? (
                            <>
                              <span>Expand Ingredients ({dish.ingredients.length})</span>
                              <ChevronDown className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              <span>Collapse Ingredients</span>
                              <ChevronUp className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      </div>

                      {/* Stacked Ingredients List */}
                      {!isCollapsed && (
                        <div className="p-3 space-y-2.5">
                          {dish.ingredients.length === 0 ? (
                            <div className="text-center py-5 text-xs text-slate-400 dark:text-zinc-500">
                              No ingredients yet. Use the text box below or the tabs above to add foods.
                            </div>
                          ) : (
                            dish.ingredients.map((ing, ingIdx) => {
                              const effectiveGrams = Math.round((ing.gramsEstimate || 100) * multiplier);
                              const dynamicEval = getDynamicIngredientRating(ing.name, effectiveGrams, ing.rating);
                              const currentRating = dynamicEval.rating;
                              const safeLimitNote = dynamicEval.note || ing.servingLimitNote || getSafeLimitNote(ing.name);
                              const portionText = formatIngredientPortion(ing.amount, ing.unit, ing.gramsEstimate, multiplier);
                              const buttonText = formatPortionButton(ing.amount, ing.unit, ing.gramsEstimate, multiplier);
                              const hasSub = Array.isArray(ing.subIngredients) && ing.subIngredients.length > 0;
                              const isSauceExpanded = expandedSauceIds.has(ing.id || String(ingIdx));

                              return (
                                <div key={ing.id || ingIdx} className="space-y-1.5">
                                  <div
                                    className={`w-full min-h-[54px] px-3.5 py-2 rounded-xl border flex items-center justify-between gap-3 transition-colors ${
                                      hasSub
                                        ? 'bg-slate-100/80 dark:bg-zinc-900/80 border-slate-300 dark:border-zinc-700'
                                        : 'bg-slate-50 dark:bg-zinc-950 border border-slate-200/80 dark:border-zinc-800/80'
                                    }`}
                                  >
                                    {/* Left Side: Tag + Name + FODMAP Pill + Natural Portion + Sauce Toggle */}
                                    <div className="flex flex-col min-w-0 flex-1 pr-2">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setInspectingIngredientItem({
                                              item: ing,
                                              effectiveGrams,
                                              dishTitle: dish.title,
                                            })
                                          }
                                          className="text-sm font-semibold text-slate-900 dark:text-zinc-100 hover:text-indigo-600 dark:hover:text-indigo-400 leading-snug text-left truncate flex items-center gap-1.5"
                                          title="Click to inspect 6-FODMAP side scale"
                                        >
                                          <span className="truncate">{ing.name}</span>
                                        </button>

                                        {/* FODMAP Level Badge Button */}
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setInspectingIngredientItem({
                                              item: ing,
                                              effectiveGrams,
                                              dishTitle: dish.title,
                                            })
                                          }
                                          className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase font-mono shrink-0 flex items-center gap-1 transition-all ${
                                            currentRating === 'low'
                                              ? 'bg-emerald-100 dark:bg-emerald-950/70 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-200'
                                              : currentRating === 'moderate'
                                              ? 'bg-amber-100 dark:bg-amber-950/70 text-amber-800 dark:text-amber-200 hover:bg-amber-200'
                                              : 'bg-rose-100 dark:bg-rose-950/70 text-rose-800 dark:text-rose-200 hover:bg-rose-200'
                                          }`}
                                          title="Click to view 6-FODMAP side scale analysis"
                                        >
                                          <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                                          <span>{currentRating}</span>
                                        </button>

                                        {hasSub && (
                                          <button
                                            type="button"
                                            onClick={() => toggleSauceExpanded(ing.id || String(ingIdx))}
                                            className="px-2 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800/60 text-indigo-700 dark:text-indigo-300 text-[10px] font-bold flex items-center gap-1 transition-all hover:bg-indigo-100"
                                          >
                                            <span>{ing.subIngredients?.length} sub-ingredients</span>
                                            {isSauceExpanded ? (
                                              <ChevronUp className="w-3 h-3 shrink-0" />
                                            ) : (
                                              <ChevronDown className="w-3 h-3 shrink-0" />
                                            )}
                                          </button>
                                        )}
                                      </div>
                                      <div className="text-[11px] font-mono text-slate-500 dark:text-zinc-400 pl-0.5 pt-0.5 leading-tight flex items-center gap-2">
                                        <span>{portionText}</span>
                                        {ing.primaryCulprit && ing.rating !== 'low' && (
                                          <span className="text-[10px] text-rose-600 dark:text-rose-400 font-semibold">
                                            • {ing.primaryCulprit}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Right Side: Natural Portion Button & Delete */}
                                    <div className="flex items-center gap-2 shrink-0">
                                      <button
                                        onClick={() =>
                                          setEditingIngredient({
                                            dishIndex: dishIdx,
                                            ingIndex: ingIdx,
                                            id: ing.id,
                                            name: ing.name,
                                            amount: ing.amount,
                                            unit: ing.unit,
                                            gramsEstimate: ing.gramsEstimate,
                                            servingLimitNote: ing.servingLimitNote,
                                            rating: ing.rating,
                                            primaryCulprit: ing.primaryCulprit,
                                          })
                                        }
                                        className="min-h-[44px] px-3.5 py-1.5 rounded-xl bg-white hover:bg-slate-100 dark:bg-zinc-900 dark:hover:bg-zinc-800 border border-slate-200 dark:border-zinc-700/60 text-xs font-semibold text-slate-800 dark:text-zinc-200 flex flex-col items-center justify-center leading-tight transition-colors shadow-xs"
                                      >
                                        <span className="capitalize">{buttonText.main}</span>
                                        <span className="text-[10px] text-slate-400 dark:text-zinc-500 font-normal">
                                          {buttonText.sub}
                                        </span>
                                      </button>

                                      <button
                                        onClick={() => handleRemoveIngredient(dishIdx, ingIdx)}
                                        aria-label="Delete Ingredient"
                                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-rose-50 dark:bg-zinc-900 dark:hover:bg-rose-950/40 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 border border-slate-200/60 dark:border-zinc-800/80 transition-colors"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Expandable Sub-Ingredients Accordion */}
                                  {hasSub && isSauceExpanded && (
                                    <div className="ml-4 pl-3 border-l-2 border-indigo-200 dark:border-indigo-800/80 space-y-1.5 py-1 animate-in fade-in duration-150">
                                      <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-400 flex items-center justify-between pb-0.5">
                                        <span>Sub-ingredient Breakdown:</span>
                                      </div>
                                      {ing.subIngredients?.map((sub, subIdx) => {
                                        const subEffGrams = Math.max(0.1, Math.round((sub.gramsEstimate || 10) * multiplier * 10) / 10);
                                        return (
                                          <div
                                            key={sub.id || subIdx}
                                            className="px-3 py-1.5 rounded-lg bg-white dark:bg-zinc-900/90 border border-slate-200/70 dark:border-zinc-800 flex items-center justify-between text-xs"
                                          >
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setInspectingIngredientItem({
                                                  item: sub,
                                                  effectiveGrams: subEffGrams,
                                                  dishTitle: `${dish.title} (${ing.name})`,
                                                })
                                              }
                                              className="flex items-center gap-2 min-w-0 hover:text-indigo-600 dark:hover:text-indigo-400 text-left"
                                            >
                                              <span
                                                className={`w-2 h-2 rounded-full shrink-0 ${
                                                  sub.rating === 'high'
                                                    ? 'bg-rose-500'
                                                    : sub.rating === 'moderate'
                                                    ? 'bg-amber-500'
                                                    : 'bg-emerald-500'
                                                }`}
                                              />
                                              <span className="font-medium text-slate-800 dark:text-zinc-200 truncate">
                                                {sub.name}
                                              </span>
                                              <span className="text-[10px] font-mono text-slate-400 dark:text-zinc-500">
                                                ({subEffGrams}g)
                                              </span>
                                              {sub.primaryCulprit && sub.rating !== 'low' && (
                                                <span className="text-[10px] text-rose-600 dark:text-rose-400 font-mono">
                                                  ({sub.primaryCulprit})
                                                </span>
                                              )}
                                            </button>

                                            <button
                                              type="button"
                                              onClick={() =>
                                                setInspectingIngredientItem({
                                                  item: sub,
                                                  effectiveGrams: subEffGrams,
                                                  dishTitle: `${dish.title} (${ing.name})`,
                                                })
                                              }
                                              className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase font-mono ${
                                                sub.rating === 'high'
                                                  ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 hover:bg-rose-200'
                                                  : sub.rating === 'moderate'
                                                  ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 hover:bg-amber-200'
                                                  : 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200'
                                              }`}
                                              title="Click to view 6-FODMAP side scale analysis"
                                            >
                                              {sub.rating}
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}

                          {/* Inline Add Ingredient Input with Autocomplete List anchored above */}
                          <div className="relative pt-1">
                            {focusedDishIdx === dishIdx && (inlineIngInputs[dishIdx] || '').trim().length >= 1 && (
                              (() => {
                                const q = (inlineIngInputs[dishIdx] || '').trim();
                                const matches = searchFodmapDatabase(q).slice(0, 5);
                                if (matches.length === 0) return null;
                                return (
                                  <div className="absolute bottom-full left-0 right-0 mb-2 p-1.5 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 shadow-xl z-30 max-h-56 overflow-y-auto space-y-1">
                                    <div className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 flex items-center justify-between">
                                      <span>Matching Base Foods</span>
                                      <span className="font-mono text-[9px]">Tap to Add</span>
                                    </div>
                                    {matches.map((food) => (
                                      <button
                                        key={food.id}
                                        type="button"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          handleAddDirectFoodItem(dishIdx, food);
                                          setInlineIngInputs((prev) => ({ ...prev, [dishIdx]: '' }));
                                          setFocusedDishIdx(dishIdx);
                                          inputRefs.current[dishIdx]?.focus();
                                        }}
                                        className="w-full px-3 py-2 rounded-xl flex items-center justify-between text-left hover:bg-slate-100 dark:hover:bg-zinc-800/80 transition-colors"
                                      >
                                        <div className="min-w-0 pr-2">
                                          <div className="text-xs font-semibold text-slate-900 dark:text-zinc-100 truncate">
                                            {food.name}
                                          </div>
                                          {isFodmapEnabled && (
                                            <div className="text-[10px] text-slate-400 dark:text-zinc-500 font-mono truncate">
                                              {food.servings?.standard?.title || (food.servings?.low?.title ? `Safe: ${food.servings.low.title}` : '')}
                                              {food.safeGrams && food.safeGrams < 500 && food.rating !== 'low' ? ` • Safe limit: ${food.safeGrams}g` : ''}
                                            </div>
                                          )}
                                        </div>
                                        {isFodmapEnabled && (
                                          <span
                                            className={`px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wider uppercase shrink-0 ${
                                              food.rating === 'high'
                                                ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/70 dark:text-rose-300'
                                                : food.rating === 'moderate'
                                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/70 dark:text-amber-300'
                                                : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/70 dark:text-emerald-300'
                                            }`}
                                          >
                                            {food.rating === 'high' ? 'HIGH' : food.rating === 'moderate' ? 'MED' : 'LOW'}
                                          </span>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                );
                              })()
                            )}

                            <form
                              onSubmit={(e) => {
                                e.preventDefault();
                                const val = (inlineIngInputs[dishIdx] || '').trim();
                                if (!val) return;
                                handleAddIngredientToDish(dishIdx, val);
                                setInlineIngInputs((prev) => ({ ...prev, [dishIdx]: '' }));
                                setFocusedDishIdx(dishIdx);
                                inputRefs.current[dishIdx]?.focus();
                              }}
                              className="flex items-center gap-2"
                            >
                              <input
                                ref={(el) => {
                                  inputRefs.current[dishIdx] = el;
                                }}
                                type="text"
                                value={inlineIngInputs[dishIdx] || ''}
                                onFocus={() => setFocusedDishIdx(dishIdx)}
                                onBlur={() => setTimeout(() => setFocusedDishIdx(null), 200)}
                                onChange={(e) =>
                                  setInlineIngInputs((prev) => ({ ...prev, [dishIdx]: e.target.value }))
                                }
                                placeholder="Add ingredient..."
                                className="flex-1 h-11 px-3.5 rounded-xl bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 text-xs text-slate-900 dark:text-zinc-100 placeholder-slate-400 dark:placeholder-zinc-500 focus:outline-none focus:border-slate-400 dark:focus:border-zinc-600"
                              />
                              <button
                                type="submit"
                                disabled={!(inlineIngInputs[dishIdx] || '').trim()}
                                className="h-11 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 text-xs font-semibold flex items-center gap-1.5 transition-colors shrink-0 shadow-xs"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                <span>Add</span>
                              </button>
                            </form>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            activeTab === null && (
              <div className="p-8 text-center rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-dashed border-slate-200 dark:border-zinc-800 space-y-3">
                <Utensils className="w-8 h-8 mx-auto text-slate-400 dark:text-zinc-500" />
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-zinc-300">Your Plate is Empty</h4>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 max-w-xs mx-auto">
                    Select Search, Scan, AI, or Library from the tabs above to add foods or ingredients.
                  </p>
                </div>
                <button
                  onClick={() => setActiveTab('search')}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-slate-900 dark:bg-zinc-100 text-white dark:text-zinc-950 text-xs font-semibold shadow-sm"
                >
                  <Search className="w-3.5 h-3.5" />
                  <span>Start Searching</span>
                </button>
              </div>
            )
          )}

          {/* =========================================================
              5. BOTTOM CLINICAL ASSESSMENT CARDS
              ========================================================= */}
          {hasPlateItems && (
            <div className="space-y-4">
              {/* CARD: CONFIRMED FREE-FROM CARD */}
              <div className="p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-zinc-200">
                    Confirmed Free-From (Exclusions)
                  </h4>
                </div>

                <div className="flex flex-wrap gap-2">
                  {FREE_FROM_OPTIONS.map((tag) => {
                    const isSelected = confirmedFreeFrom.includes(tag);
                    return (
                      <button
                        key={tag}
                        onClick={() => {
                          setConfirmedFreeFrom((prev) =>
                            isSelected ? prev.filter((t) => t !== tag) : [...prev, tag]
                          );
                        }}
                        className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-medium transition-all ${
                          isSelected
                            ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 font-semibold shadow'
                            : 'bg-slate-50 text-slate-600 border border-slate-200 hover:text-slate-900 hover:bg-slate-100 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-800 dark:hover:bg-zinc-800 dark:hover:text-zinc-100'
                        }`}
                      >
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* DIRECT VISIBLE DELETE BUTTON WHEN EDITING AN EXISTING MEAL */}
              {Boolean(initialMeal?.id) && (
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMealSettings(true);
                      setShowDeleteConfirm(true);
                    }}
                    className="w-full min-h-[46px] px-4 rounded-2xl border border-rose-200 dark:border-rose-900/60 bg-rose-50/60 hover:bg-rose-100/80 dark:bg-rose-950/30 dark:hover:bg-rose-950/60 text-rose-700 dark:text-rose-300 text-xs font-semibold flex items-center justify-center gap-2 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete This Meal</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* =========================================================
            6. STICKY BOTTOM SAVE ACTION (Large Ergonomic Button)
            ========================================================= */}
        {hasPlateItems && (
          <footer className="p-4 bg-white/95 dark:bg-zinc-950/95 border-t border-slate-200 dark:border-zinc-800 sticky bottom-0 z-20">
            <button
              onClick={handleSaveMeal}
              className="w-full min-h-[50px] py-3.5 px-6 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-zinc-200 dark:text-zinc-950 font-bold text-sm tracking-wide flex items-center justify-center gap-2 active:scale-[0.99] transition-all shadow-lg"
            >
              <Check className="w-5 h-5" />
              <span>Log Foods ({dishes.reduce((acc, d) => acc + d.ingredients.length, 0)} items)</span>
            </button>
          </footer>
        )}

        {/* =========================================================
            7. MODALS & POPUPS
            ========================================================= */}
        {/* FODMAP Stacking Detail Modal */}
        <FodmapStackingDetailModal
          isOpen={showStackingDetail}
          onClose={() => setShowStackingDetail(false)}
          report={stackingReport}
        />

        {/* Time and Date Picker Sheet */}
        {showTimePicker && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/75 p-4 backdrop-blur-sm">
            <div className="w-full max-w-sm bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-2xl p-5 space-y-4 shadow-2xl text-slate-900 dark:text-zinc-100">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-zinc-100">
                  Log Date & Time
                </h3>
                <button
                  onClick={() => setShowTimePicker(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:text-zinc-400 dark:hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3 text-xs">
                <div>
                  <label className="block text-slate-600 dark:text-zinc-400 mb-1 font-medium">Date</label>
                  <input
                    type="date"
                    value={entryDate}
                    onChange={(e) => setEntryDate(e.target.value)}
                    className="w-full h-11 px-3 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 text-slate-900 dark:text-zinc-200"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 dark:text-zinc-400 mb-1 font-medium">Time</label>
                  <input
                    type="time"
                    value={entryTime}
                    onChange={(e) => handleTimeChange(e.target.value)}
                    className="w-full h-11 px-3 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 text-slate-900 dark:text-zinc-200"
                  />
                </div>
              </div>

              <button
                onClick={() => setShowTimePicker(false)}
                className="w-full min-h-[46px] rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 font-semibold text-xs tracking-wide"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* Meal Settings Sheet */}
        {(showMealSettings || showCategoryPicker) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/75 p-4 backdrop-blur-sm animate-in fade-in duration-150">
            <div className="w-full max-w-sm max-h-[90vh] overflow-y-auto bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-2xl p-5 space-y-5 shadow-2xl text-slate-900 dark:text-zinc-100">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-zinc-800">
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-zinc-100">
                    Meal Settings
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-zinc-400">
                    {initialMeal?.id ? 'Category, time & meal management' : 'Configure meal category & time'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowMealSettings(false);
                    setShowCategoryPicker(false);
                    setShowDeleteConfirm(false);
                  }}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:text-zinc-400 dark:hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* 1. Meal Category */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400 mb-2">
                  Meal Category
                </label>
                <div className="grid grid-cols-2 gap-1.5">
                  {MEAL_CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setMealCategory(cat)}
                      className={`min-h-[44px] px-3.5 rounded-xl text-left text-xs font-semibold flex items-center justify-between transition-colors ${
                        mealCategory === cat
                          ? 'bg-slate-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-sm'
                          : 'bg-slate-100 dark:bg-zinc-900 text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-800'
                      }`}
                    >
                      <span>{cat}</span>
                      {mealCategory === cat && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* 2. Date & Time */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400 mb-2">
                  Date & Time
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="block text-[10px] text-slate-400 dark:text-zinc-500 mb-1">Date</span>
                    <input
                      type="date"
                      value={entryDate}
                      onChange={(e) => setEntryDate(e.target.value)}
                      className="w-full h-11 px-3 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 text-xs font-mono font-semibold text-slate-900 dark:text-zinc-100 focus:outline-none focus:border-slate-400"
                    />
                  </div>
                  <div>
                    <span className="block text-[10px] text-slate-400 dark:text-zinc-500 mb-1">Time</span>
                    <input
                      type="time"
                      value={entryTime}
                      onChange={(e) => handleTimeChange(e.target.value)}
                      className="w-full h-11 px-3 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 text-xs font-mono font-semibold text-slate-900 dark:text-zinc-100 focus:outline-none focus:border-slate-400"
                    />
                  </div>
                </div>
              </div>

              {/* 3. Delete Meal Section */}
              {(initialMeal?.id || hasPlateItems) && (
                <div className="pt-2 border-t border-slate-100 dark:border-zinc-800 space-y-2">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400">
                    Danger Zone
                  </label>

                  {!showDeleteConfirm ? (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full min-h-[46px] px-4 rounded-xl bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-950/70 border border-rose-200/80 dark:border-rose-900/60 text-rose-700 dark:text-rose-300 text-xs font-semibold flex items-center justify-center gap-2 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>{initialMeal?.id ? 'Delete Logged Meal' : 'Clear Entire Plate'}</span>
                    </button>
                  ) : (
                    <div className="p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-900/60 space-y-3 animate-in fade-in duration-100">
                      <div className="text-xs text-rose-900 dark:text-rose-200 font-medium">
                        {initialMeal?.id
                          ? 'Are you sure you want to permanently delete this meal from your timeline?'
                          : 'Are you sure you want to remove all dishes and ingredients from this meal?'}
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(false)}
                          disabled={isDeleting}
                          className="flex-1 min-h-[42px] rounded-lg bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 text-slate-700 dark:text-zinc-300 text-xs font-semibold hover:bg-slate-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={isDeleting}
                          onClick={async () => {
                            try {
                              setIsDeleting(true);
                              if (initialMeal?.id && onDeleteMeal) {
                                await onDeleteMeal(initialMeal.id);
                              } else {
                                setDishes([
                                  {
                                    id: `dish-${Date.now()}-1`,
                                    title: 'MAIN DISH',
                                    portionMultiplier: 1.0,
                                    ingredients: [],
                                  },
                                ]);
                                setNotes('');
                                setConfirmedFreeFrom([]);
                              }
                              setShowMealSettings(false);
                              setShowCategoryPicker(false);
                              setShowDeleteConfirm(false);
                              onClose();
                            } finally {
                              setIsDeleting(false);
                            }
                          }}
                          className="flex-1 min-h-[42px] rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                        >
                          {isDeleting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                          <span>Confirm Delete</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Done Button */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowMealSettings(false);
                    setShowCategoryPicker(false);
                    setShowDeleteConfirm(false);
                  }}
                  className="w-full min-h-[46px] rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 text-xs font-semibold transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Dish Name Modal */}
        {editingDishIndex !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/75 p-4 backdrop-blur-sm">
            <div className="w-full max-w-sm bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-2xl p-5 space-y-4 shadow-2xl text-slate-900 dark:text-zinc-100">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 dark:text-zinc-100">
                Rename Dish
              </h3>
              <input
                type="text"
                value={editDishName}
                onChange={(e) => setEditDishName(e.target.value)}
                placeholder="e.g. RISOTTO, GREEN SALAD"
                className="w-full h-11 px-3.5 rounded-xl bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 text-xs text-slate-900 dark:text-zinc-100 uppercase focus:outline-none focus:border-slate-400 dark:focus:border-zinc-500"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingDishIndex(null)}
                  className="flex-1 min-h-[44px] rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-700 dark:text-zinc-300 text-xs font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const copy = [...dishes];
                    const clean = editDishName.trim();
                    if (clean) {
                      copy[editingDishIndex].title = clean.toUpperCase();
                      copy[editingDishIndex].isCustomTitle = true;
                    } else {
                      const auto = synthesizeDishTitle(copy[editingDishIndex].ingredients);
                      copy[editingDishIndex].title = auto ? auto.toUpperCase() : 'MAIN DISH';
                      copy[editingDishIndex].isCustomTitle = false;
                    }
                    setDishes(copy);
                    setEditingDishIndex(null);
                  }}
                  className="flex-1 min-h-[44px] rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 text-xs font-semibold transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Interactive Dish Yield & Portion Calculator Modal */}
        {editingPortionDish && (
          <DishPortionModal
            dishTitle={dishes[editingPortionDish.dishIndex]?.title || 'Dish'}
            currentMultiplier={editingPortionDish.currentMultiplier}
            recipeServings={editingPortionDish.recipeServings}
            portionsEaten={editingPortionDish.portionsEaten}
            dishGramsTotal={
              dishes[editingPortionDish.dishIndex]?.ingredients.reduce(
                (sum, ing) => sum + (ing.gramsEstimate || 0),
                0
              ) || 0
            }
            onClose={() => setEditingPortionDish(null)}
            onSave={(newMultiplier, recipeServings, portionsEaten) => {
              const copy = [...dishes];
              if (copy[editingPortionDish.dishIndex]) {
                copy[editingPortionDish.dishIndex].portionMultiplier = newMultiplier;
                copy[editingPortionDish.dishIndex].recipeServings = recipeServings;
                copy[editingPortionDish.dishIndex].portionsEaten = portionsEaten;
              }
              setDishes(copy);
              setEditingPortionDish(null);
            }}
          />
        )}

        {/* Single Unified Ingredient Portion & Stacking Adjuster */}
        {editingIngredient && (
          <IngredientPortionModal
            editingIngredient={editingIngredient}
            onClose={() => setEditingIngredient(null)}
            onSave={(finalAmount, finalUnit, finalGrams) => {
              const copy = [...dishes];
              const targetDish = copy[editingIngredient.dishIndex];
              const targetIng = targetDish?.ingredients[editingIngredient.ingIndex];
              if (targetIng) {
                targetIng.amount = finalAmount;
                targetIng.unit = finalUnit;
                targetIng.gramsEstimate = finalGrams;
                const dynamicEval = getDynamicIngredientRating(targetIng.name, finalGrams, targetIng.rating);
                targetIng.rating = dynamicEval.rating;
              }
              setDishes(copy);
              setEditingIngredient(null);
            }}
            onSwapIngredient={(dishIndex, ingIndex, newFood) => {
              const copy = [...dishes];
              const targetDish = copy[dishIndex];
              if (targetDish && targetDish.ingredients[ingIndex]) {
                const realistic = getRealisticCulinaryServing(newFood.name, newFood);
                const dynamicEval = getDynamicIngredientRating(newFood.name, realistic.grams, newFood.rating, newFood);
                targetDish.ingredients[ingIndex] = {
                  ...targetDish.ingredients[ingIndex],
                  name: newFood.name,
                  canonicalName: newFood.name,
                  amount: realistic.amount,
                  unit: realistic.unit,
                  gramsEstimate: realistic.grams,
                  rating: dynamicEval.rating,
                  primaryCulprit: dynamicEval.primaryCulprit || derivePrimaryCulprit(newFood.fodmaps),
                  servingLimitNote: newFood.servings?.low?.title
                    ? `Low at ${newFood.servings.low.title}${newFood.servings.low.grams ? ` (${newFood.servings.low.grams}g)` : ''}`
                    : newFood.notes || realistic.servingLimitNote,
                  fodmaps: newFood.fodmaps,
                };
                if (!targetDish.isCustomTitle) {
                  const autoTitle = synthesizeDishTitle(targetDish.ingredients);
                  if (autoTitle) {
                    targetDish.title = autoTitle.toUpperCase();
                  }
                }
                setDishes(copy);
                setEditingIngredient(null);
              }
            }}
            onSaveCustomToBase={(dishIndex, ingIndex, customFood) => {
              const copy = [...dishes];
              const targetDish = copy[dishIndex];
              if (targetDish && targetDish.ingredients[ingIndex]) {
                targetDish.ingredients[ingIndex] = {
                  ...targetDish.ingredients[ingIndex],
                  name: customFood.name,
                  canonicalName: customFood.name,
                  rating: customFood.rating,
                  primaryCulprit: customFood.primaryCulprit,
                  servingLimitNote: customFood.safeGrams ? `Safe Low FODMAP at ${customFood.safeGrams}g` : undefined,
                };
                setDishes(copy);
                setEditingIngredient(null);
              }
            }}
          />
        )}

        {/* 6-FODMAP Side Scale Inspector Modal */}
        {inspectingIngredientItem && (
          <FodmapSideScaleInspectorModal
            item={inspectingIngredientItem.item}
            effectiveGrams={inspectingIngredientItem.effectiveGrams}
            dishTitle={inspectingIngredientItem.dishTitle}
            onClose={() => setInspectingIngredientItem(null)}
          />
        )}

        {/* Dish Total Weight Scaler Modal */}
        {editingDishWeight && (
          <DishTotalWeightModal
            dishTitle={editingDishWeight.dishTitle}
            currentGrams={editingDishWeight.currentGrams}
            onClose={() => setEditingDishWeight(null)}
            onSave={(targetGrams) => {
              const copy = [...dishes];
              const targetDish = copy[editingDishWeight.dishIndex];
              if (targetDish) {
                copy[editingDishWeight.dishIndex] = scaleDishTotalWeight(targetDish, targetGrams);
                setDishes(copy);
              }
              setEditingDishWeight(null);
            }}
          />
        )}
      </div>
    </Sheet>
  );
};
